import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { Filter, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import DashboardLayout from "../../../components/dashboard-layout/DashboardLayout";
import styles from "../../../styles/__labEarningPayment.module.scss";
import logo from "../../../assets/images/logo.png";
import user from "../../../assets/images/user.png";
import { IoMdSettings } from "react-icons/io";
import { PiChatCircleTextLight } from "react-icons/pi";
import DialogReschedule from "../../../components/dialog-reschedule/DialogReschedule";
import DialogCreateFollowUp from "../../../components/dialog-create/DialogCreateFollowUp";
import DialogManageAvailability from "../../../components/dialog-manage-availability/DialogManageAvailability";
import DialogPatientRegistration from "../../../components/patient-registration/DialogPatientRegistration";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchDoctorAppointmentsReducer } from "../../../reducer/appointments/fetchDoctorAppointmentsReducer";
import { fetchEarningsMetricsReducer } from "../../../reducer/earnings/fetchEarningsMetricsReducer";
import { fetchEarningsListReducer } from "../../../reducer/earnings/fetchEarningsListReducer";
import { fetchProviderSettlementsReducer } from "../../../reducer/settlement/fetchProviderSettlementsReducer";
import { joinDoctorAppointmentReducer } from "../../../reducer/joinDoctorAppointment/joinDoctorAppointmentReducer";
import AppointmentDetails from "../../../components/appointmentDetails/AppointmentDetails";
import Loader from "../../../components/loader/Loader";
import LabDashboardLayout from "../../../components/labmod-dashboard-layout/LabModDashboardLayout";
import { FiDownload } from "react-icons/fi";
import NurseDashboardLayout from "../../../components/nurse-dashboard-layout/NurseDashboardLayout";
import PhysioDashboardLayout from "../../../components/physio-dashboard-layout/PhysioDashboardLayout";
import { formatDateNumeric as tzDateNumeric } from "../../../utils/datetime";

// The "Initiate Withdrawal" button is hidden for centers for now. Set to true
// to show it again (the withdraw page and its route are unchanged).
const SHOW_INITIATE_WITHDRAWAL = false;

const PhysioEarningPayment = ({ onLogout, onNavigateToDashboard }) => {
  const { t, i18n } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("Earnings");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState("");
  const itemsPerPage = 10;
  const [date, setDate] = useState(null);
  // Financial metrics date-range filter (From / To). Scopes every metric card to the period;
  // either end may be left empty (open-ended), both empty = all time. Same as lab / imaging.
  const [metricsDateFrom, setMetricsDateFrom] = useState(null);
  const [metricsDateTo, setMetricsDateTo] = useState(null);
  const [openDialogReschedule, setOpenDialogReschedule] = useState(false);
  const [openDialogCreateFollowUp, setOpenDialogCreateFollowUp] =
    useState(false);
  const [openDialogManageAvailability, setOpenDialogManageAvailability] =
    useState(false);
  const [openDialogPatientRegistration, setOpenDialogPatientRegistration] =
    useState(false);
    const [status, setStatus] = useState(true);
  // Front-face service-name tooltip (position:fixed clone) — mirrors the
  // order-management page so the tooltip is never clipped by the table's
  // horizontal scroll box.
  const [svcTip, setSvcTip] = useState(null);


  const dispatch = useDispatch();
  const {
    data: apiAppointments,
    isLoading: loading,
    error,
    totalPages: reduxTotalPages,
  } = useSelector((state) => state.fetchDoctorAppointments);

  const {
    data: earningsMetrics,
    isLoading: loadingMetrics,
  } = useSelector((state) => state.earningsMetrics);

  const {
    data: earningsListData,
    pagination: earningsListPagination,
    isLoading: loadingEarningsList,
  } = useSelector((state) => state.earningsList);

  // ── Withdrawals / Payouts tab (settlement history) ────────────────────────
  const [withdrawalsPage, setWithdrawalsPage] = useState(1);
  const [withdrawalSearch, setWithdrawalSearch] = useState("");
  const [withdrawalStatusFilter, setWithdrawalStatusFilter] = useState("");
  const [withdrawalMethodFilter, setWithdrawalMethodFilter] = useState("");
  const [withdrawalDate, setWithdrawalDate] = useState(null);
  const [debouncedWithdrawalSearch, setDebouncedWithdrawalSearch] = useState("");

  const settlementRecords = useSelector((state) => state.payoutAccount?.settlements) || [];
  const loadingWithdrawals = useSelector((state) => state.payoutAccount?.isLoadingSettlements);
  const withdrawalsPagination = useSelector(
    (state) => state.payoutAccount?.settlementsPagination
  ) || { total: 0, page: 1, limit: 10, total_pages: 1 };

  // Use Redux state for totalPages, fallback to local state if needed
  const totalPages = reduxTotalPages || 1;
  const today = new Date();
  const formatted = format(today, "MMMM dd, yyyy");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  // Static placeholder appointments (kept for reference)

  // Bind appointments from API (normalize shape for the existing UI)
  const rawData = Array.isArray(apiAppointments) ? apiAppointments : (apiAppointments?.orders || apiAppointments?.data?.orders || []);
  const appointments = rawData.map((a) => {
    const patient = a?.patient || {};
    const name = `${patient?.full_name || ""} ${
      patient?.surname || ""
    }`.trim();
    const avatar = patient?.avatar_presigned_url || patient?.avatar || user;
    // Determine typeIcon based on consultation_type
    const typeIcon =
      a.consultation_type === "video"
        ? "video"
        : a.consultation_type === "audio"
          ? "mic"
          : "message";
    const type = a.consultation_type === "video" ? "Online Consult" : "On-Site";
    return {
      id: a.id,
      name,
      avatar,
      location: a?.location || a?.onsite_details || "",
      date: a?.appointment_date || a?.booking_date || "",
      time:
        a?.formatted_time ||
        (a?.appointment_time_slot
          ? a.appointment_time_slot.charAt(0).toUpperCase() +
            a.appointment_time_slot.slice(1)
          : ""),
      typeIcon,
      type,
      status: a?.status || "",
      raw: a,
    };
  });

  // Sort appointments (filtering is done server-side)
  const sortedAppointments = useMemo(() => {
    // Sort appointments
    const sorted = [...appointments].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [appointments, sortField, sortDirection]);

  // Pagination
  // const totalPages = Math.ceil(
  //   filteredAndSortedAppointments.length / itemsPerPage
  // );
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const paginatedAppointments = filteredAndSortedAppointments.slice(
  //   startIndex,
  //   startIndex + itemsPerPage
  // );

  const paginatedAppointments = sortedAppointments;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const formatDate = (dateObj) => {
    if (!dateObj) return null;
    try {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Format status text for display
  const formatStatusText = (status) => {
    if (!status) return "";
    // Convert to display format
    const statusDisplayMap = {
      pending: "Pending",
      upcoming: "Upcoming",
      ongoing: "Ongoing",
      completed: "Completed",
      cancelled: "Cancelled",
      reschedule_requested: "Reschedule Requested",
      "reschedule-requested": "Reschedule Requested",
    };

    const lowerStatus = status.toLowerCase();
    if (statusDisplayMap[lowerStatus]) {
      return statusDisplayMap[lowerStatus];
    }

    // Capitalize first letter of each word for other statuses
    return status
      .split(/[_\s-]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Normalize status value for API (convert display format to API format)
  const normalizeStatusForAPI = (status) => {
    if (!status || status === "All") return null;
    // Convert display format to API format
    const statusMap = {
      Pending: "pending",
      Upcoming: "upcoming",
      Ongoing: "ongoing",
      Completed: "completed",
      Cancelled: "cancelled",
      "Reschedule Requested": "reschedule_requested",
      reschedule_requested: "reschedule_requested",
      pending: "pending",
      upcoming: "upcoming",
      ongoing: "ongoing",
      completed: "completed",
      cancelled: "cancelled",
    };
    return statusMap[status] || status.toLowerCase().replace(/\s+/g, "_");
  };

  // Function to fetch appointments with current filters
  // Optional parameters allow overriding filter values (useful for tab clicks and reset)
  // Use undefined as default to distinguish between "not provided" and "explicitly null"
  const fetchAppointmentsWithFilters = (
    pageNum = currentPage,
    statusOverride = undefined,
    dateOverride = undefined,
    searchOverride = undefined,
  ) => {
    // Use override values if provided (including null), otherwise use current state
    // undefined means "use state value", null means "explicitly clear/no filter"
    const dateToUse = dateOverride !== undefined ? dateOverride : date;
    const statusToUse =
      statusOverride !== undefined ? statusOverride : statusFilter;
    const searchToUse =
      searchOverride !== undefined ? searchOverride : searchTerm;

    const formattedDate = formatDate(dateToUse);
    const normalizedStatus = normalizeStatusForAPI(statusToUse);

    // Build params object - only include defined values
    const params = {
      page: pageNum,
      limit: itemsPerPage,
    };

    // Only add date if it's a valid formatted date string (not null/undefined/empty)
    if (
      formattedDate &&
      formattedDate !== null &&
      formattedDate !== "null" &&
      formattedDate !== ""
    ) {
      params.date = formattedDate;
    }

    if (searchToUse && searchToUse.trim()) {
      params.search = searchToUse.trim();
    }

    if (normalizedStatus) {
      params.status = normalizedStatus;
    }

    dispatch(fetchDoctorAppointmentsReducer(params))
      .then((res) => {
        setCurrentPage(pageNum);
        // Total pages will be updated in Redux state automatically
      })
      .catch((error) => {
        console.error("❌ Error fetching appointments:", error);
      });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    fetchAppointmentsWithFilters(page);
  };

  const resetFilters = () => {
    // Reset state values first
    setSearchTerm("");
    setStatusFilter("All");
    setDate(null);
    setCurrentPage(1);

    // Re-fetch earnings list with no filters
    dispatch(fetchEarningsListReducer({ page: 1, limit: 10 }));
  };

  const handleNavigate = (page) => {
    if (page === "dashboard") {
      onNavigateToDashboard();
    }
  };

  const joinAppointment = async (id) => {
    try {
      const result = await dispatch(
        joinDoctorAppointmentReducer({ appointmentId: id }),
      ).unwrap();
      console.log("result:", result);
      // reducer returns response.data.data (normalized) so join_url should be on result
      const joinUrl = result?.join_url || result?.data?.join_url;
      if (joinUrl) {
        // open in a new tab
        window.open(joinUrl, "_blank");
      } else {
        console.warn(
          "No join_url received from joinDoctorAppointmentReducer",
          result,
        );
      }
    } catch (err) {
      console.error("Failed to join appointment:", err);
    }
  };

  // Initial load with default filters
  useEffect(() => {
    dispatch(fetchEarningsListReducer({ page: 1, limit: 10 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Financial metrics: fetched on mount and re-fetched whenever the From/To range changes.
  // Dates go out as the LOCAL calendar day (yyyy-MM-dd), never toISOString(), which would
  // shift a local-midnight date to the previous day in UTC+3.
  useEffect(() => {
    dispatch(
      fetchEarningsMetricsReducer({
        date_from: metricsDateFrom
          ? format(metricsDateFrom, "yyyy-MM-dd")
          : undefined,
        date_to: metricsDateTo ? format(metricsDateTo, "yyyy-MM-dd") : undefined,
      }),
    );
  }, [dispatch, metricsDateFrom, metricsDateTo]);

  // Front-face service-name tooltip via event delegation (mirrors the
  // order-management page). The inline `.testNames` markup is read on hover and
  // a single position:fixed tooltip is anchored to the hovered cell so it is
  // never clipped by the table's horizontal scroll box.
  useEffect(() => {
    const cls = styles["testNames"];
    if (!cls) return;

    const showFor = (host) => {
      const listEl = host.querySelector("." + styles["testNamesList"]);
      if (!listEl) return;
      const items = Array.from(listEl.querySelectorAll("li"))
        .slice(1) // skip the "Services" header li
        .map((li) => li.textContent.trim())
        .filter(Boolean);
      if (!items.length) return;
      const rect = host.getBoundingClientRect();
      const belowSpace = window.innerHeight - rect.bottom;
      const flipUp = belowSpace < 200;
      const y = flipUp ? rect.top - 8 : rect.bottom + 4;
      setSvcTip({ items, x: rect.left, y, flipUp });
    };

    const onOver = (e) => {
      const host = e.target.closest("." + cls);
      if (host) showFor(host);
    };
    const onOut = (e) => {
      const host = e.target.closest("." + cls);
      if (host && !host.contains(e.relatedTarget)) setSvcTip(null);
    };
    const onScroll = () => setSvcTip(null);

    document.addEventListener("mouseover", onOver);
    document.addEventListener("mouseout", onOut);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mouseout", onOut);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, []);

  // Debounce the withdrawals search input
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedWithdrawalSearch(withdrawalSearch), 500);
    return () => clearTimeout(tid);
  }, [withdrawalSearch]);

  // Reset to first page when any withdrawal filter changes
  useEffect(() => {
    setWithdrawalsPage(1);
  }, [debouncedWithdrawalSearch, withdrawalStatusFilter, withdrawalMethodFilter, withdrawalDate]);

  // Fetch withdrawal/settlement history when the Payouts tab is active
  useEffect(() => {
    if (activeTab !== "Payouts") return;
    const params = { provider_type: "physiotherapy", page: withdrawalsPage, limit: itemsPerPage };
    if (debouncedWithdrawalSearch.trim()) params.search = debouncedWithdrawalSearch.trim();
    if (withdrawalStatusFilter && withdrawalStatusFilter !== "ALL") params.status = withdrawalStatusFilter;
    if (withdrawalMethodFilter && withdrawalMethodFilter !== "ALL") params.payment_method = withdrawalMethodFilter;
    if (withdrawalDate) {
      params.date_from = format(withdrawalDate, "yyyy-MM-dd'T'00:00:00");
      params.date_to = format(withdrawalDate, "yyyy-MM-dd'T'23:59:59");
    }
    dispatch(fetchProviderSettlementsReducer(params));
    const onSettlementUpdate = () => dispatch(fetchProviderSettlementsReducer(params));
    window.addEventListener("settlement_updated", onSettlementUpdate);
    return () => window.removeEventListener("settlement_updated", onSettlementUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, withdrawalsPage, debouncedWithdrawalSearch, withdrawalStatusFilter, withdrawalMethodFilter, withdrawalDate, dispatch]);

  const withdrawalStatusLabel = (s) => {
    switch ((s || "").toLowerCase()) {
      case "pending": return "Requested";
      case "approved": return "Processing";
      case "completed": return "Completed";
      case "rejected": return "Failed";
      case "cancelled": return "Cancelled";
      default: return s || "";
    }
  };
  const withdrawalStatusClass = (s) => {
    switch ((s || "").toLowerCase()) {
      case "pending": return "awaiting-payment";
      case "approved": return "in-progress";
      case "completed": return "payment-complete";
      default: return "default";
    }
  };
  const withdrawalAccountLabel = (acc) => {
    if (!acc || typeof acc !== "object") return "—";
    const raw = acc.card_number || acc.account_number || acc.billing_account || acc.iban || "";
    const last4 = String(raw).replace(/\s+/g, "").slice(-4);
    if (last4) return `**** ${last4}`;
    return acc.holder_name || acc.bank_name || "—";
  };
  const formatWithdrawAmount = (val) => Number(val || 0).toLocaleString("en-US");
  const clearWithdrawalFilters = () => {
    setWithdrawalSearch("");
    setWithdrawalStatusFilter("");
    setWithdrawalMethodFilter("");
    setWithdrawalDate(null);
    setWithdrawalsPage(1);
  };
  const withdrawalsTotal = withdrawalsPagination.total || 0;
  const withdrawalsTotalPages = withdrawalsPagination.total_pages || 1;

  // useEffect(() => {
  //   dispatch(fetchDoctorAppointmentsReducer()).then((res)=>{
  //   setTotalPages(res?.payload?.pagination?.total_pages)
  // })

  //   // dispatch(joinDoctorAppointmentReducer({appointmentId:2}))
  // }, []);

  const handlePageChange1 = (data) => {
    const nextPage = data + 1;
    fetchAppointmentsWithFilters(nextPage);
  };

  const handlePageChange11 = (data) => {
    const prevPage = data - 1;
    if (prevPage >= 1) {
      fetchAppointmentsWithFilters(prevPage);
    }
  };
  const handleBackDetail = (state, data) => {
    setShowDetails(state);
    setSelectedAppointment(data);
  };

  const handleReschedules = (data) => {
    setOpenDialogReschedule(true);
    setSelectedAppointment(data);
  };

  const CreateFollowUp = (data) => {
    setOpenDialogCreateFollowUp(true);
    setSelectedAppointment(data);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Upcoming":
        return "status-upcoming";
      case "Completed":
        return "status-payment";
      case "Cancelled":
        return "status-cancelled";
      case "Reschedule Requested":
        return "status-reschedule";
      case "Pending":
        return "status-cod";
      case "COD":
        return "status-cod";
      case "Payment Complete":
        return "status-payment";
      case "Collected":
        return "status-payment";
      case "Lab":
        return "status-labtype";
      case "Home Visit":
        return "status-labtype";
      case "Imaging":
        return "status-imagingtype";
      case "In-Progress":
        return "status-labtype";
      case "Scheduled":
        return "status-labtype";
      case "On-Site":
        return "status-imagingtype";
      case "Urgent":
        return "status-red";
      case "Technician Dispatched":
        return "status-dispatched";
      case "Sent":
        return "status-dispatched";
      case "Awaiting Payment":
        return "status-awaiting";
      case "Order Accepted":
        return "status-accepted";
      default:
        return "";
    }
  };

  const TotalEarningsIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.6727 20.3635C23.8953 20.3635 25.697 19.6906 25.697 18.8605C25.697 18.0304 23.8953 17.3574 21.6727 17.3574C19.4502 17.3574 17.6485 18.0304 17.6485 18.8605C17.6485 19.6906 19.4502 20.3635 21.6727 20.3635Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M25.697 18.8604V26.0846C25.697 26.9088 23.9031 27.5876 21.6728 27.5876C19.4425 27.5876 17.6486 26.9088 17.6486 26.0846V18.8604C17.6486 19.6846 19.4425 20.3634 21.6728 20.3634C23.9031 20.3634 25.697 19.6846 25.697 18.8604Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M31.0304 21.2853C31.0304 22.1095 29.2364 22.7883 27.0061 22.7883C26.5213 22.7883 26.0849 22.7398 25.697 22.6913V19.8792C26.1334 19.8307 26.5697 19.7822 27.0061 19.7822C29.2364 19.7822 31.0304 20.461 31.0304 21.2853Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M31.0304 21.2852V26.0852C31.0304 26.9094 29.2364 27.5882 27.0061 27.5882C25.988 27.5882 25.0668 27.4427 24.3395 27.2003C25.1637 26.9094 25.6971 26.5215 25.6971 26.0852V22.6912C26.1334 22.7397 26.5698 22.7882 27.0061 22.7882C29.2364 22.7882 31.0304 22.1094 31.0304 21.2852Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M25.697 21.2852C25.697 22.1094 23.9031 22.7882 21.6728 22.7882C19.4425 22.7882 17.6486 22.1094 17.6486 21.2852"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M25.697 23.709C25.697 24.5332 23.9031 25.212 21.6728 25.212C19.4425 25.212 17.6486 24.5332 17.6486 23.709"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M31.0303 23.709C31.0303 24.5332 29.2364 25.212 27.0061 25.212C26.5697 25.212 26.1818 25.1635 25.7455 25.1635"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M25.697 18.8605C25.697 18.0362 23.903 17.3574 21.6727 17.3574C19.4424 17.3574 17.6485 18.0362 17.6485 18.8605V26.0847C17.6485 26.9089 19.4424 27.5877 21.6727 27.5877C22.6909 27.5877 23.6121 27.4423 24.3394 27.1998C25.0667 27.4423 25.9879 27.5877 27.0061 27.5877C29.2364 27.5877 31.0303 26.9089 31.0303 26.0847V21.2847C31.0303 20.4605 29.2364 19.7817 27.0061 19.7817C26.5697 19.7817 26.1333 19.8301 25.697 19.8786V18.8605ZM25.697 18.8605C25.697 19.0544 25.6 19.1998 25.4546 19.3938C25.6 19.2483 25.697 19.0544 25.697 18.8605Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M31.0303 23.709C31.0303 24.5332 29.2364 25.212 27.0061 25.212C26.5697 25.212 26.1818 25.1635 25.7455 25.1635"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17.2122 15.0787L18.8606 15.9999L11.1031 20.509L10.9091 20.6059L10.6667 20.5575L1.21215 15.0787L0.969727 14.9332L8.92124 10.3271L10.5697 11.2484L17.2122 15.0787Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M29.0424 10.1333L22.4485 13.9636L20.8485 12.9939L14.206 9.16362L12.5576 8.24241L15.1757 6.73938L18.9091 4.50908L19.103 4.41211L19.2969 4.55756L28.7515 10.0364L29.0424 10.1333Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M17.2121 15.0787L11.297 18.5211C11.006 18.3757 10.6667 18.3757 10.3757 18.5211L5.18787 15.5151C5.62423 15.2726 5.62423 14.6423 5.18787 14.3999L10.5697 11.2969L17.2121 15.0787Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M24.7273 10.7156L20.8485 12.9944L14.2061 9.16408L18.7151 6.5459C19.0061 6.69135 19.3454 6.69135 19.6364 6.5459L24.7273 9.50347C24.2909 9.79438 24.2909 10.4732 24.7273 10.7156Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M22.4485 13.9634L19.9757 15.4179L18.8606 16.0482L17.2121 15.0786L10.5697 11.2482L8.9212 10.327L12.5576 8.24219L14.1576 9.1634L20.8485 12.9937L22.4485 13.9634Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M20.4121 8.92131C19.8788 8.6304 19.0061 8.6304 18.5212 8.92131C17.9879 9.21222 17.9879 9.74555 18.5212 10.0365C19.0545 10.3274 19.9273 10.3274 20.4121 10.0365C20.9455 9.69707 20.9455 9.21222 20.4121 8.92131Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M11.3939 14.351C10.8606 14.0601 9.98788 14.0601 9.50303 14.351C8.96969 14.6419 8.96969 15.1752 9.50303 15.4661C10.0364 15.7571 10.9091 15.7571 11.3939 15.4661C11.8788 15.1752 11.9273 14.6419 11.3939 14.351Z"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M0.969727 14.9336V17.2609V17.3094L10.8606 23.0306L16.097 19.976"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M23.1758 15.9025L29.0424 12.5086V12.4116V10.1328"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M0.969727 17.2607V17.3092V19.588L10.8606 25.3092L16.1455 22.2547"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M25.7455 16.6788L29.0425 14.7879V12.5091V12.4121"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M0.969727 19.5391V21.8663L10.8606 27.5875L16.194 24.533"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M29.0424 14.6914V17.0672L27.0546 18.2308"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
      <path
        d="M18.8606 16.2911V16.0487L22.4485 13.9639V15.9517"
        stroke="black"
        stroke-width="0.727273"
        stroke-miterlimit="10"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );

  const CodIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M26.6666 23.0588L25.3083 22.245C24.8942 21.9973 24.4205 21.8666 23.938 21.8663H21.8937C21.4752 21.8656 21.0585 21.9195 20.6539 22.0262L14.4945 23.6796C13.8211 23.8656 13.2802 24.3671 13.0442 25.0247L7.62652 22.0937C7.1872 21.8437 6.67288 21.7588 6.17652 21.8541C5.98928 21.8872 5.80777 21.9468 5.6372 22.0312C5.25205 22.2156 4.93251 22.5135 4.72106 22.8846C4.53616 23.2077 4.43903 23.5736 4.43903 23.9458C4.4333 24.7057 4.8359 25.4098 5.49319 25.7906L15.2031 31.6002C15.625 31.8614 16.1109 31.9997 16.607 31.9997H24.1216C24.4572 31.9999 24.7898 31.9364 25.1018 31.8129L26.6666 31.195V31.9997H31.9999V21.333H26.6666V23.0588ZM24.7098 30.821C24.5231 30.8947 24.3239 30.9327 24.1231 30.933H16.607C16.3065 30.9327 16.0119 30.8481 15.7572 30.6887L6.03408 24.8723C5.70361 24.6814 5.50126 24.3278 5.50387 23.9463C5.50465 23.7593 5.55387 23.5755 5.64684 23.413C5.75309 23.2288 5.91298 23.0812 6.10491 22.99C6.18955 22.9489 6.27913 22.9197 6.37158 22.9036C6.43902 22.8911 6.50751 22.8846 6.576 22.8846C6.75934 22.884 6.93954 22.932 7.09866 23.0231L12.9734 26.1976C12.9802 26.2301 12.9802 26.2622 12.9888 26.2947C13.2414 27.2257 14.0859 27.8723 15.0507 27.8734C15.3476 27.8463 15.6416 27.7937 15.9296 27.7166L21.4708 26.2338L21.1958 25.2033L15.6726 26.6796C15.4294 26.7356 15.1156 26.7997 15.0507 26.8051C14.569 26.8059 14.1466 26.484 14.0197 26.0195C14.0026 25.9549 13.9924 25.8887 13.9888 25.8221C13.9888 25.8221 13.9846 25.6929 13.9888 25.6476C14.0294 25.2015 14.3419 24.827 14.7734 24.707L20.9302 23.0598C21.2445 22.9757 21.5684 22.933 21.8937 22.933H23.938C24.2276 22.933 24.5117 23.0117 24.7598 23.1604L26.6666 24.302V30.0476L24.7098 30.821ZM27.7333 22.3997H30.9333V30.933H27.7333V22.3997Z"
        fill="black"
      />
      <path
        d="M21.9969 4.26668L19.1359 1.40601C18.2377 0.502623 17.0156 -0.00362627 15.7419 1.95563e-05H9.03671C8.62291 0.000540389 8.21484 0.0971549 7.84427 0.28153L6.64895 0.879446C6.40937 1.00002 6.14323 1.05757 5.87526 1.04689L5.33333 1.02788V1.95563e-05H0V11.7333H5.33333V10.1333H6.2401L6.56276 10.3466C7.46562 10.9461 8.51692 11.2846 9.59999 11.3242V18.1333H32V4.26668H21.9969ZM4.26666 10.6667H1.06667V1.06669H4.26666V10.6667ZM7.15312 9.45913L6.55989 9.06668H5.33333V2.09507L5.83619 2.11304C6.28281 2.12892 6.72604 2.03283 7.12578 1.83361L8.32005 1.2357C8.54244 1.1245 8.78802 1.06669 9.03671 1.06669H15.7419C16.7325 1.06382 17.6833 1.45757 18.3818 2.15991L20.4885 4.26668H16.0875L14.793 2.97241L14.0391 3.72632L17.8065 7.49403C18.225 7.91069 18.2263 8.58777 17.8096 9.00652C17.3927 9.42501 16.7156 9.42632 16.2971 9.00965L13.1505 5.86304L12.7771 6.18309C11.7461 7.05861 10.2391 7.081 9.18229 6.23621L8.87317 5.98569L8.20104 6.81434L8.51354 7.06772C8.84036 7.32892 9.20729 7.53569 9.59999 7.67996V10.257C8.72708 10.2182 7.88098 9.94246 7.15312 9.45913ZM14.7398 8.95991L15.5445 9.76486C16.3799 10.5878 17.7211 10.5891 18.5578 9.76746C17.775 10.9797 18.0838 12.593 19.2591 13.4302C20.4344 14.2675 22.0601 14.0323 22.95 12.8966C23.8398 11.7607 23.6792 10.1258 22.5849 9.18517C21.4909 8.24455 19.8502 8.33074 18.8609 9.38074C19.2252 8.80861 19.2904 8.09559 19.0357 7.46668H26.1776C26.407 8.80861 27.4581 9.85965 28.8 10.0891V12.3109C27.4581 12.5404 26.407 13.5914 26.1776 14.9333H15.4224C15.193 13.5914 14.1419 12.5404 12.8 12.3109V10.0891C13.5622 9.96069 14.2516 9.55939 14.7398 8.95991ZM19.2 11.2C19.2 10.3164 19.9164 9.60001 20.8 9.60001C21.6836 9.60001 22.4 10.3164 22.4 11.2C22.4 12.0836 21.6836 12.8 20.8 12.8C19.9164 12.8 19.2 12.0836 19.2 11.2ZM30.9333 17.0667H10.6667V7.9034C11.5122 7.96694 12.3552 7.75132 13.0667 7.29012L13.9766 8.20001C13.5763 8.74376 12.9419 9.06538 12.2667 9.06668H11.7333V13.3333H12.2667C13.4448 13.3333 14.4 14.2886 14.4 15.4667V16H27.2V15.4667C27.2 14.2886 28.1552 13.3333 29.3333 13.3333H29.8666V9.06668H29.3333C28.1552 9.06668 27.2 8.11147 27.2 6.93335V6.40002H18.2208L17.1542 5.33335H30.9333V17.0667Z"
        fill="black"
      />
    </svg>
  );

  const OnlinePaymentIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M16 5.5C13.9233 5.5 11.8932 6.11581 10.1665 7.26957C8.4398 8.42332 7.09399 10.0632 6.29927 11.9818C5.50455 13.9004 5.29661 16.0116 5.70176 18.0484C6.1069 20.0852 7.10693 21.9562 8.57538 23.4246C10.0438 24.8931 11.9148 25.8931 13.9516 26.2982C15.9884 26.7034 18.0996 26.4955 20.0182 25.7007C21.9368 24.906 23.5767 23.5602 24.7304 21.8335C25.8842 20.1068 26.5 18.0767 26.5 16C26.4987 13.2156 25.392 10.5457 23.4232 8.57684C21.4543 6.608 18.7844 5.50132 16 5.5ZM24.497 11.75H20.28C20.0378 10.3294 19.5703 8.95661 18.895 7.6835C18.7153 7.3636 18.5085 7.05966 18.277 6.775C19.6113 7.10645 20.8581 7.7227 21.9317 8.58146C23.0054 9.44021 23.8805 10.5211 24.497 11.75ZM19.6665 16C19.6685 17.0874 19.592 18.1736 19.4375 19.25H12.5625C12.2572 17.0941 12.2572 14.9059 12.5625 12.75H19.4375C19.592 13.8264 19.6685 14.9126 19.6665 16ZM16 6.5C16.7735 6.5 17.4455 7.157 18.0255 8.1775C18.6206 9.2971 19.0384 10.5023 19.264 11.75H12.736C12.9615 10.5023 13.3794 9.2971 13.9745 8.1775C14.5545 7.157 15.2265 6.5 16 6.5ZM13.723 6.775C13.4915 7.05964 13.2847 7.36359 13.105 7.6835C12.4298 8.9566 11.9622 10.3294 11.72 11.75H7.503C8.11942 10.5211 8.99452 9.4401 10.0682 8.58134C11.1419 7.72258 12.3887 7.10636 13.723 6.775ZM6.5 16C6.49867 14.8916 6.69211 13.7915 7.0715 12.75H11.555C11.2597 14.9066 11.2597 17.0934 11.555 19.25H7.0715C6.69212 18.2085 6.49868 17.1084 6.5 16ZM7.503 20.25H11.72C11.9622 21.6706 12.4298 23.0434 13.105 24.3165C13.2847 24.6364 13.4915 24.9403 13.723 25.225C12.3887 24.8936 11.1419 24.2773 10.0683 23.4185C8.99462 22.5598 8.11951 21.4789 7.503 20.25ZM16 25.5C15.2265 25.5 14.5545 24.843 13.9745 23.8225C13.3794 22.7029 12.9615 21.4977 12.736 20.25H19.264C19.0384 21.4977 18.6206 22.7029 18.0255 23.8225C17.4455 24.843 16.7735 25.5 16 25.5ZM18.277 25.225C18.5085 24.9403 18.7153 24.6364 18.895 24.3165C19.5703 23.0434 20.0378 21.6706 20.28 20.25H24.497C23.8806 21.4789 23.0055 22.5599 21.9318 23.4187C20.8582 24.2774 19.6113 24.8936 18.277 25.225ZM20.445 19.25C20.5941 18.1731 20.6681 17.0872 20.6665 16C20.6681 14.9128 20.5941 13.8269 20.445 12.75H24.9285C25.6905 14.8497 25.6905 17.1503 24.9285 19.25H20.445Z"
        fill="black"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M3.99995 10.884C3.60213 10.884 3.2206 10.726 2.93929 10.4447C2.65799 10.1634 2.49995 9.78182 2.49995 9.384C2.49995 9.25139 2.44728 9.12422 2.35351 9.03045C2.25974 8.93668 2.13256 8.884 1.99995 8.884C1.86735 8.884 1.74017 8.93668 1.6464 9.03045C1.55263 9.12422 1.49995 9.25139 1.49995 9.384C1.50031 9.9602 1.69949 10.5186 2.06386 10.965C2.42824 11.4114 2.93549 11.7183 3.49995 11.834V12.769C3.49995 12.9016 3.55263 13.0288 3.6464 13.1226C3.74017 13.2163 3.86735 13.269 3.99995 13.269C4.13256 13.269 4.25974 13.2163 4.35351 13.1226C4.44728 13.0288 4.49995 12.9016 4.49995 12.769V11.834C5.10753 11.7106 5.64759 11.3659 6.0153 10.8667C6.383 10.3676 6.55212 9.74959 6.48981 9.13275C6.4275 8.51591 6.13822 7.94423 5.67812 7.52869C5.21802 7.11314 4.61993 6.88338 3.99995 6.884C3.70328 6.884 3.41327 6.79603 3.1666 6.6312C2.91993 6.46638 2.72767 6.23211 2.61414 5.95802C2.5006 5.68394 2.4709 5.38234 2.52878 5.09136C2.58665 4.80039 2.72952 4.53312 2.93929 4.32334C3.14907 4.11356 3.41635 3.9707 3.70732 3.91282C3.99829 3.85494 4.29989 3.88465 4.57398 3.99818C4.84807 4.11171 5.08234 4.30397 5.24716 4.55064C5.41198 4.79732 5.49995 5.08733 5.49995 5.384C5.49995 5.51661 5.55263 5.64379 5.6464 5.73755C5.74017 5.83132 5.86735 5.884 5.99995 5.884C6.13256 5.884 6.25974 5.83132 6.35351 5.73755C6.44728 5.64379 6.49995 5.51661 6.49995 5.384C6.4996 4.8078 6.30042 4.24937 5.93604 3.80301C5.57167 3.35665 5.06442 3.0497 4.49995 2.934V2C4.49995 1.86739 4.44728 1.74021 4.35351 1.64645C4.25974 1.55268 4.13256 1.5 3.99995 1.5C3.86735 1.5 3.74017 1.55268 3.6464 1.64645C3.55263 1.74021 3.49995 1.86739 3.49995 2V2.934C2.89238 3.05737 2.35232 3.4021 1.98461 3.90126C1.61691 4.40043 1.44779 5.01841 1.5101 5.63525C1.57241 6.25209 1.86169 6.82377 2.32179 7.23931C2.78189 7.65486 3.37998 7.88462 3.99995 7.884C4.39778 7.884 4.77931 8.04204 5.06062 8.32334C5.34192 8.60464 5.49995 8.98618 5.49995 9.384C5.49995 9.78182 5.34192 10.1634 5.06062 10.4447C4.77931 10.726 4.39778 10.884 3.99995 10.884Z"
        fill="black"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M28 21.1149C28.3978 21.1149 28.7793 21.273 29.0606 21.5543C29.3419 21.8356 29.5 22.2171 29.5 22.6149C29.5 22.7476 29.5526 22.8747 29.6464 22.9685C29.7402 23.0623 29.8673 23.1149 30 23.1149C30.1326 23.1149 30.2597 23.0623 30.3535 22.9685C30.4473 22.8747 30.5 22.7476 30.5 22.6149C30.5001 22.0386 30.3011 21.48 29.9366 21.0335C29.5722 20.587 29.0646 20.2802 28.5 20.1649V19.2314C28.5 19.0988 28.4473 18.9717 28.3535 18.8779C28.2597 18.7841 28.1326 18.7314 28 18.7314C27.8673 18.7314 27.7402 18.7841 27.6464 18.8779C27.5526 18.9717 27.5 19.0988 27.5 19.2314V20.1654C26.8924 20.2888 26.3523 20.6335 25.9846 21.1327C25.6169 21.6319 25.4478 22.2499 25.5101 22.8667C25.5724 23.4835 25.8617 24.0552 26.3218 24.4708C26.7819 24.8863 27.38 25.1161 28 25.1154C28.2966 25.1154 28.5866 25.2034 28.8333 25.3682C29.08 25.5331 29.2722 25.7673 29.3858 26.0414C29.4993 26.3155 29.529 26.6171 29.4711 26.9081C29.4133 27.1991 29.2704 27.4663 29.0606 27.6761C28.8508 27.8859 28.5836 28.0287 28.2926 28.0866C28.0016 28.1445 27.7 28.1148 27.4259 28.0013C27.1518 27.8877 26.9176 27.6955 26.7528 27.4488C26.5879 27.2021 26.5 26.9121 26.5 26.6154C26.5 26.4828 26.4473 26.3557 26.3535 26.2619C26.2597 26.1681 26.1326 26.1154 26 26.1154C25.8673 26.1154 25.7402 26.1681 25.6464 26.2619C25.5526 26.3557 25.5 26.4828 25.5 26.6154C25.4998 27.1918 25.6988 27.7504 26.0633 28.1969C26.4278 28.6434 26.9353 28.9502 27.5 29.0654V30.0004C27.5 30.1331 27.5526 30.2602 27.6464 30.354C27.7402 30.4478 27.8673 30.5004 28 30.5004C28.1326 30.5004 28.2597 30.4478 28.3535 30.354C28.4473 30.2602 28.5 30.1331 28.5 30.0004V29.0649C29.1075 28.9416 29.6476 28.5968 30.0153 28.0977C30.383 27.5985 30.5521 26.9805 30.4898 26.3637C30.4275 25.7469 30.1382 25.1752 29.6781 24.7596C29.218 24.3441 28.6199 24.1143 28 24.1149C27.6021 24.1149 27.2206 23.9569 26.9393 23.6756C26.658 23.3943 26.5 23.0128 26.5 22.6149C26.5 22.2171 26.658 21.8356 26.9393 21.5543C27.2206 21.273 27.6021 21.1149 28 21.1149Z"
        fill="black"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M10.2145 3.8C12.2725 2.82489 14.5436 2.38609 16.8168 2.52442C19.0899 2.66276 21.291 3.37372 23.2156 4.59119C25.1402 5.80866 26.7255 7.49309 27.8243 9.48779C28.9231 11.4825 29.4995 13.7227 29.5 16C29.5 16.1326 29.5527 16.2598 29.6464 16.3536C29.7402 16.4473 29.8674 16.5 30 16.5C30.1326 16.5 30.2598 16.4473 30.3536 16.3536C30.4473 16.2598 30.5 16.1326 30.5 16C30.4995 13.554 29.8803 11.1478 28.7001 9.00533C27.5199 6.86286 25.817 5.0537 23.7498 3.74613C21.6826 2.43857 19.3183 1.67509 16.8767 1.52671C14.4352 1.37833 11.9958 1.84989 9.7855 2.8975C9.72369 2.92404 9.66788 2.96278 9.62139 3.0114C9.57491 3.06002 9.53871 3.11751 9.51497 3.18045C9.49123 3.24339 9.48043 3.31047 9.48322 3.37767C9.48601 3.44488 9.50233 3.51083 9.53121 3.57159C9.56009 3.63234 9.60093 3.68664 9.65128 3.73123C9.70164 3.77583 9.76047 3.80981 9.82427 3.83114C9.88806 3.85246 9.95551 3.8607 10.0226 3.85535C10.0896 3.84999 10.1549 3.83117 10.2145 3.8Z"
        fill="black"
      />
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M21.7855 28.2C19.7275 29.1751 17.4564 29.6139 15.1832 29.4756C12.9101 29.3372 10.709 28.6263 8.7844 27.4088C6.85985 26.1913 5.27446 24.5069 4.1757 22.5122C3.07694 20.5175 2.5005 18.2773 2.5 16C2.5 15.8674 2.44732 15.7402 2.35355 15.6464C2.25979 15.5527 2.13261 15.5 2 15.5C1.86739 15.5 1.74021 15.5527 1.64645 15.6464C1.55268 15.7402 1.5 15.8674 1.5 16C1.50093 18.4458 2.12039 20.8517 3.30077 22.9938C4.48115 25.1359 6.1841 26.9448 8.25124 28.252C10.3184 29.5593 12.6826 30.3226 15.1239 30.4708C17.5652 30.6191 20.0044 30.1475 22.2145 29.1C22.2754 29.0728 22.3302 29.0337 22.3757 28.9851C22.4212 28.9364 22.4566 28.8791 22.4797 28.8166C22.5027 28.7541 22.5131 28.6876 22.5101 28.621C22.5071 28.5544 22.4908 28.4891 22.4622 28.4289C22.4336 28.3687 22.3933 28.3148 22.3435 28.2705C22.2938 28.2261 22.2357 28.1921 22.1727 28.1705C22.1096 28.1489 22.0429 28.1401 21.9764 28.1446C21.9099 28.1492 21.845 28.167 21.7855 28.197V28.2Z"
        fill="black"
      />
    </svg>
  );

  const AdminFeesIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M29.608 2.81833C29.3264 2.53384 28.9909 2.3083 28.6212 2.15489C28.2514 2.00149 27.8548 1.92327 27.4545 1.92483H7.9395C7.20717 1.92569 6.50506 2.21693 5.98716 2.7347C5.46925 3.25247 5.17781 3.95449 5.17675 4.68683V23.4191H2C1.86739 23.4191 1.74021 23.4718 1.64645 23.5655C1.55268 23.6593 1.5 23.7865 1.5 23.9191V26.7473C1.50119 27.6296 1.85225 28.4754 2.47618 29.0992C3.10011 29.723 3.94598 30.0738 4.82825 30.0748H22.3638C23.1711 30.0736 23.9449 29.7523 24.5158 29.1814C25.0866 28.6105 25.4078 27.8366 25.409 27.0293V16.7826H30C30.1326 16.7826 30.2598 16.7299 30.3536 16.6361C30.4473 16.5424 30.5 16.4152 30.5 16.2826V4.96958C30.5015 4.56976 30.4233 4.17364 30.2702 3.80431C30.117 3.43498 29.8919 3.09981 29.608 2.81833ZM5.67675 29.0748H4.82825C4.21111 29.0741 3.61945 28.8287 3.183 28.3924C2.74654 27.956 2.50093 27.3645 2.5 26.7473V24.4191H18.7525V26.7473C18.7519 27.6183 19.094 28.4546 19.7048 29.0756L5.67675 29.0748ZM24.409 4.96958V27.0301C24.4078 27.5722 24.192 28.0917 23.8087 28.4751C23.4254 28.8584 22.9058 29.0743 22.3638 29.0756H22.0807C21.4635 29.0749 20.8717 28.8293 20.4352 28.3928C19.9988 27.9564 19.7532 27.3646 19.7525 26.7473V23.9191C19.7525 23.7865 19.6998 23.6593 19.6061 23.5655C19.5123 23.4718 19.3851 23.4191 19.2525 23.4191H6.1775V4.68683C6.17823 4.21974 6.3641 3.77199 6.69438 3.44171C7.02466 3.11143 7.47241 2.92555 7.9395 2.92483H25.2048C24.6934 3.48308 24.4095 4.21252 24.409 4.96958ZM29.5 15.7826H25.409V4.96958C25.4096 4.42726 25.6253 3.90732 26.0088 3.52385C26.3922 3.14037 26.9122 2.92467 27.4545 2.92408C27.9966 2.92527 28.5162 3.14116 28.8996 3.5245C29.2829 3.90785 29.4988 4.42744 29.5 4.96958V15.7826Z"
        fill="black"
      />
      <path
        d="M11.2656 10.6809H11.1486C10.9834 10.6968 10.8186 10.6477 10.6892 10.5438C10.5598 10.44 10.476 10.2898 10.4558 10.1251C10.4558 9.9925 10.4031 9.86532 10.3094 9.77156C10.2156 9.67779 10.0884 9.62511 9.95581 9.62511C9.8232 9.62511 9.69602 9.67779 9.60226 9.77156C9.50849 9.86532 9.45581 9.9925 9.45581 10.1251C9.46701 10.479 9.59634 10.819 9.8232 11.0908C10.0501 11.3627 10.3614 11.5508 10.7076 11.6251V11.8951C10.7076 12.0277 10.7602 12.1549 10.854 12.2487C10.9478 12.3424 11.075 12.3951 11.2076 12.3951C11.3402 12.3951 11.4673 12.3424 11.5611 12.2487C11.6549 12.1549 11.7076 12.0277 11.7076 11.8951V11.6251C12.0535 11.5505 12.3644 11.3623 12.591 11.0904C12.8175 10.8186 12.9465 10.4788 12.9576 10.1251V9.91011C12.9381 9.47984 12.7491 9.07476 12.432 8.78336C12.1148 8.49196 11.6952 8.33791 11.2648 8.35486H11.1478C10.9828 8.37084 10.8181 8.32178 10.6887 8.21812C10.5593 8.11446 10.4755 7.96439 10.4551 7.79986V7.58861C10.4755 7.42408 10.5593 7.27401 10.6887 7.17035C10.8181 7.06669 10.9828 7.01763 11.1478 7.03361H11.2648C11.4298 7.01763 11.5946 7.06669 11.724 7.17035C11.8533 7.27401 11.9372 7.42408 11.9576 7.58861C11.9576 7.72122 12.0102 7.84839 12.104 7.94216C12.1978 8.03593 12.325 8.08861 12.4576 8.08861C12.5902 8.08861 12.7173 8.03593 12.8111 7.94216C12.9049 7.84839 12.9576 7.72122 12.9576 7.58861C12.9464 7.23472 12.817 6.89475 12.5902 6.6229C12.3633 6.35106 12.052 6.16296 11.7058 6.08861V5.81836C11.7058 5.68575 11.6531 5.55857 11.5594 5.46481C11.4656 5.37104 11.3384 5.31836 11.2058 5.31836C11.0732 5.31836 10.946 5.37104 10.8523 5.46481C10.7585 5.55857 10.7058 5.68575 10.7058 5.81836V6.08911C10.36 6.16381 10.049 6.35204 9.82251 6.62386C9.596 6.89568 9.46692 7.23546 9.45581 7.58911V7.80011C9.47536 8.23033 9.66434 8.63535 9.9815 8.9267C10.2987 9.21805 10.7182 9.37206 11.1486 9.35511H11.2656C11.4306 9.3392 11.5953 9.38832 11.7247 9.49202C11.8541 9.59572 11.9379 9.74581 11.9583 9.91036V10.1251C11.9381 10.2898 11.8544 10.44 11.7249 10.5438C11.5955 10.6477 11.4307 10.6968 11.2656 10.6809Z"
        fill="black"
      />
      <path
        d="M15.0103 9.35742H21.5153C21.6479 9.35742 21.775 9.30474 21.8688 9.21097C21.9626 9.11721 22.0153 8.99003 22.0153 8.85742C22.0153 8.72481 21.9626 8.59764 21.8688 8.50387C21.775 8.4101 21.6479 8.35742 21.5153 8.35742H15.0103C14.8776 8.35742 14.7505 8.4101 14.6567 8.50387C14.5629 8.59764 14.5103 8.72481 14.5103 8.85742C14.5103 8.99003 14.5629 9.11721 14.6567 9.21097C14.7505 9.30474 14.8776 9.35742 15.0103 9.35742Z"
        fill="black"
      />
      <path
        d="M15.0103 6.5293H21.5153C21.6479 6.5293 21.775 6.47662 21.8688 6.38285C21.9626 6.28908 22.0153 6.16191 22.0153 6.0293C22.0153 5.89669 21.9626 5.76951 21.8688 5.67574C21.775 5.58198 21.6479 5.5293 21.5153 5.5293H15.0103C14.8776 5.5293 14.7505 5.58198 14.6567 5.67574C14.5629 5.76951 14.5103 5.89669 14.5103 6.0293C14.5103 6.16191 14.5629 6.28908 14.6567 6.38285C14.7505 6.47662 14.8776 6.5293 15.0103 6.5293Z"
        fill="black"
      />
      <path
        d="M15.0103 12.1855H21.5153C21.6479 12.1855 21.775 12.1329 21.8688 12.0391C21.9626 11.9453 22.0153 11.8182 22.0153 11.6855C22.0153 11.5529 21.9626 11.4258 21.8688 11.332C21.775 11.2382 21.6479 11.1855 21.5153 11.1855H15.0103C14.8776 11.1855 14.7505 11.2382 14.6567 11.332C14.5629 11.4258 14.5103 11.5529 14.5103 11.6855C14.5103 11.8182 14.5629 11.9453 14.6567 12.0391C14.7505 12.1329 14.8776 12.1855 15.0103 12.1855Z"
        fill="black"
      />
      <path
        d="M9.0708 15.0859H21.5153C21.6479 15.0859 21.7751 15.0333 21.8689 14.9395C21.9626 14.8457 22.0153 14.7185 22.0153 14.5859C22.0153 14.4533 21.9626 14.3262 21.8689 14.2324C21.7751 14.1386 21.6479 14.0859 21.5153 14.0859H9.0708C8.93819 14.0859 8.81102 14.1386 8.71725 14.2324C8.62348 14.3262 8.5708 14.4533 8.5708 14.5859C8.5708 14.7185 8.62348 14.8457 8.71725 14.9395C8.81102 15.0333 8.93819 15.0859 9.0708 15.0859Z"
        fill="black"
      />
      <path
        d="M9.0708 17.9141H21.5153C21.6479 17.9141 21.7751 17.8614 21.8689 17.7676C21.9626 17.6738 22.0153 17.5467 22.0153 17.4141C22.0153 17.2815 21.9626 17.1543 21.8689 17.0605C21.7751 16.9667 21.6479 16.9141 21.5153 16.9141H9.0708C8.93819 16.9141 8.81102 16.9667 8.71725 17.0605C8.62348 17.1543 8.5708 17.2815 8.5708 17.4141C8.5708 17.5467 8.62348 17.6738 8.71725 17.7676C8.81102 17.8614 8.93819 17.9141 9.0708 17.9141Z"
        fill="black"
      />
      <path
        d="M9.0708 20.7422H21.5153C21.6479 20.7422 21.7751 20.6895 21.8689 20.5957C21.9626 20.502 22.0153 20.3748 22.0153 20.2422C22.0153 20.1096 21.9626 19.9824 21.8689 19.8886C21.7751 19.7949 21.6479 19.7422 21.5153 19.7422H9.0708C8.93819 19.7422 8.81102 19.7949 8.71725 19.8886C8.62348 19.9824 8.5708 20.1096 8.5708 20.2422C8.5708 20.3748 8.62348 20.502 8.71725 20.5957C8.81102 20.6895 8.93819 20.7422 9.0708 20.7422Z"
        fill="black"
      />
    </svg>
  );

  const CommissionIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28.9186 20.3012H28.787C29.3605 19.73 29.7161 18.9398 29.7161 18.068C29.7161 16.3296 28.3026 14.9153 26.5651 14.9153C24.8267 14.9153 23.4124 16.3296 23.4124 18.068C23.4124 18.9398 23.7681 19.73 24.3419 20.3012H24.2116C22.5288 20.3012 21.1596 21.6703 21.1596 23.3532V24.8429C17.4145 27.1414 12.6445 26.754 9.32088 23.9065L11.6168 21.8863C11.7494 21.7697 11.7987 21.5846 11.7417 21.4175C11.6847 21.2503 11.5326 21.1339 11.3563 21.1226C9.97881 21.034 8.598 20.9462 7.22125 20.8587L5.33712 20.7389C5.219 20.7308 5.10288 20.7721 5.01544 20.8517C4.92788 20.9314 4.87644 21.0431 4.87281 21.1614L4.69419 27.0018C4.68881 27.1758 4.78731 27.3363 4.94487 27.4103C5.10237 27.4844 5.28894 27.4579 5.4195 27.3428L7.02694 25.9262C9.49525 28.1836 12.6734 29.3635 15.8904 29.3635C17.7978 29.3635 19.7187 28.9476 21.513 28.0962C21.54 28.1014 21.5679 28.1043 21.5964 28.1043H31.5319C31.7732 28.1043 31.9687 27.9088 31.9687 27.6676V23.3532C31.9688 21.6703 30.6004 20.3012 28.9186 20.3012ZM24.2859 18.068C24.2859 16.8113 25.3083 15.7888 26.5651 15.7888C27.8209 15.7888 28.8425 16.8113 28.8425 18.068C28.8425 19.1685 28.0592 20.0892 27.0212 20.3012H26.1085C25.0698 20.0892 24.2859 19.1685 24.2859 18.068ZM7.34144 25.0223C7.17794 24.8632 6.91937 24.8569 6.74819 25.0077L5.59812 26.0212L5.73212 21.6393L7.16587 21.7305C8.19213 21.7958 9.22075 21.8612 10.2486 21.9268L8.37519 23.5752C8.28288 23.6565 8.22906 23.7728 8.227 23.8958C8.22494 24.0187 8.27481 24.1368 8.36431 24.2212C10.1416 25.8945 12.4637 26.9366 14.9027 27.1554C17.0709 27.3501 19.2637 26.8886 21.1597 25.8516V27.298C16.5419 29.4997 11.0209 28.6008 7.34144 25.0223ZM31.0952 27.2308H22.0332V23.3532C22.0332 22.152 23.0104 21.1748 24.2116 21.1748H26.0319C26.2054 21.2045 26.3832 21.2208 26.5651 21.2208C26.7467 21.2208 26.9245 21.2045 27.0978 21.1748H28.9185C30.1188 21.1748 31.0952 22.152 31.0952 23.3532V27.2308ZM11.5583 13.3398C11.5583 12.1788 11.1506 10.8685 10.4103 9.65016C10.134 9.19541 9.82362 8.77278 9.4895 8.39166C11.5817 6.68353 14.2944 5.87803 16.9924 6.17703C19.6892 6.47591 22.1597 7.85109 23.8306 9.96978L21.5234 12.0005C21.3927 12.1155 21.3429 12.2971 21.3964 12.4627C21.45 12.6283 21.5968 12.7463 21.77 12.763L27.5856 13.3253C27.5997 13.3267 27.6137 13.3274 27.6277 13.3274C27.7311 13.3274 27.8316 13.2907 27.9109 13.2231C28.001 13.1463 28.0561 13.0363 28.0636 12.9182C28.1891 10.9343 28.3164 8.92391 28.4455 6.89934C28.4567 6.72303 28.3606 6.55728 28.202 6.47947C28.0434 6.40166 27.8535 6.42703 27.7209 6.54384L26.1098 7.96309C25.0744 6.68128 23.8239 5.62153 22.3871 4.80891C20.87 3.95078 19.2178 3.40722 17.4766 3.19334C14.0253 2.76916 10.6474 3.68266 7.91919 5.76741C7.89869 5.75366 7.87719 5.74122 7.85575 5.72884L8.94169 3.53803C9.02825 3.36347 8.98856 3.15259 8.84463 3.02134C8.70063 2.89009 8.487 2.87003 8.32119 2.97228C7.54119 3.45316 7.40469 3.34897 7.10569 3.12084C6.83781 2.91653 6.47106 2.63672 5.79369 2.63672C5.11619 2.63672 4.74937 2.91666 4.48163 3.12103C4.18313 3.34891 4.04688 3.45278 3.26825 2.97241C3.10225 2.86997 2.88875 2.88991 2.74475 3.02128C2.60069 3.15253 2.561 3.36341 2.64756 3.53803L3.73325 5.72841C3.4065 5.91697 3.18556 6.26959 3.18556 6.67359C3.18556 6.88372 3.24638 7.07947 3.34987 7.24603C2.53431 7.83066 1.77725 8.66553 1.179 9.65016C0.438875 10.8685 0.03125 12.1788 0.03125 13.3398C0.03125 16.5183 2.61637 19.1042 5.79381 19.1042C8.97237 19.1042 11.5583 16.5183 11.5583 13.3398ZM17.3703 4.06034C20.7278 4.47272 23.6865 6.17728 25.7013 8.85978C25.7744 8.95716 25.8848 9.01953 26.0059 9.03191C26.1275 9.04422 26.2478 9.00566 26.3391 8.92516L27.5067 7.89666C27.4102 9.41291 27.3148 10.9198 27.2203 12.4125L22.8568 11.9905L24.7122 10.3575C24.8814 10.2085 24.9094 9.95528 24.777 9.77291C22.9636 7.27641 20.1613 5.64934 17.0885 5.30884C14.1313 4.98134 11.1576 5.87447 8.87631 7.76441C8.66931 7.57503 8.45631 7.40159 8.23913 7.24591C8.34269 7.07934 8.40362 6.88366 8.40362 6.67359C8.40362 6.61684 8.39794 6.56147 8.3895 6.50703C10.9456 4.52966 14.1223 3.66153 17.3703 4.06034ZM4.27494 6.88934C4.158 6.88934 4.05925 6.79059 4.05925 6.67366C4.05925 6.55378 4.156 6.45622 4.27494 6.45622H7.22294C7.22331 6.45622 7.22369 6.45628 7.22406 6.45628C7.22431 6.45628 7.22463 6.45622 7.22494 6.45622H7.31275C7.43269 6.45622 7.53025 6.55372 7.53025 6.67366C7.53025 6.79266 7.43275 6.88934 7.31275 6.88934H4.27494ZM5.01169 3.81547C5.23512 3.64497 5.41156 3.51034 5.79388 3.51034C6.17613 3.51034 6.35269 3.64497 6.57613 3.81541C6.88906 4.05416 7.19219 4.23716 7.64794 4.18166L6.95344 5.58266H4.63613L3.94175 4.18178C4.39556 4.23716 4.69919 4.05397 5.01169 3.81547ZM0.904813 13.3398C0.904813 11.3331 2.35219 8.86597 4.15812 7.75641C4.19656 7.76053 4.23544 7.76291 4.27494 7.76291H7.31275C7.35269 7.76291 7.39213 7.76047 7.431 7.75622C9.23713 8.86572 10.6847 11.333 10.6847 13.3398C10.6847 16.0366 8.49069 18.2307 5.79388 18.2307C3.098 18.2307 0.904813 16.0367 0.904813 13.3398ZM5.79381 16.0858C5.55256 16.0858 5.35706 15.8903 5.35706 15.6491V15.0586H4.27494C4.03369 15.0586 3.83819 14.863 3.83819 14.6218C3.83819 14.3807 4.03375 14.1851 4.27494 14.1851H6.41275C6.85406 14.1851 6.87644 13.7882 6.87644 13.7085C6.87644 13.629 6.85406 13.232 6.41275 13.232H6.24475H5.34475H5.17675C4.29794 13.232 3.83825 12.5524 3.83825 11.8811C3.83825 11.2098 4.298 10.5302 5.17675 10.5302H5.35706V9.93966C5.35706 9.69841 5.55262 9.50291 5.79381 9.50291C6.035 9.50291 6.23056 9.69847 6.23056 9.93966V10.5302H7.31444C7.55569 10.5302 7.75119 10.7257 7.75119 10.9669C7.75119 11.2081 7.55562 11.4037 7.31444 11.4037H5.17675C4.73413 11.4037 4.71169 11.8013 4.71169 11.881C4.71169 11.9607 4.73413 12.3584 5.17675 12.3584H5.34475H6.24475H6.41275C7.29062 12.3584 7.74994 13.0376 7.74994 13.7085C7.74994 14.3794 7.29062 15.0586 6.41275 15.0586H6.23063V15.6491C6.23063 15.8903 6.03506 16.0858 5.79381 16.0858Z"
        fill="black"
      />
    </svg>
  );

  const NetBalanceIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.433 15.2138C18.4659 15.2707 18.5097 15.3206 18.5618 15.3605C18.614 15.4005 18.6735 15.4298 18.737 15.4468C18.865 15.481 19.0013 15.463 19.116 15.3968L20.848 14.3968C20.9629 14.3305 21.0467 14.2213 21.081 14.0932C21.1153 13.9651 21.0973 13.8286 21.031 13.7138L20.031 11.9818C19.9646 11.8676 19.8556 11.7845 19.7281 11.7504C19.6005 11.7163 19.4646 11.734 19.35 11.7998L17.618 12.7998C17.5032 12.8661 17.4194 12.9753 17.3851 13.1034C17.3508 13.2315 17.3687 13.3679 17.435 13.4828L18.433 15.2138ZM19.415 12.9138L19.915 13.7798L19.0495 14.2798L18.5495 13.4138L19.415 12.9138Z"
        fill="black"
      />
      <path
        d="M21.8971 13.2144C21.9633 13.3292 22.0725 13.4131 22.2006 13.4474C22.264 13.4644 22.3302 13.4687 22.3953 13.4602C22.4604 13.4516 22.5232 13.4303 22.5801 13.3974L24.3121 12.3974C24.4269 12.3311 24.5107 12.2219 24.545 12.0938C24.5793 11.9657 24.5614 11.8292 24.4951 11.7144L23.4951 9.98241C23.4288 9.86757 23.3196 9.78378 23.1915 9.74946C23.0634 9.71514 22.9269 9.73311 22.8121 9.79941L21.0801 10.7994C20.9652 10.8657 20.8814 10.9749 20.8471 11.103C20.8128 11.2311 20.8308 11.3676 20.8971 11.4824L21.8971 13.2144ZM22.8791 10.9144L23.3791 11.7804L22.5131 12.2804L22.0131 11.4144L22.8791 10.9144Z"
        fill="black"
      />
      <path
        d="M8.75495 14.4497C8.88756 14.6794 9.10598 14.847 9.36215 14.9156C9.61833 14.9843 9.89127 14.9483 10.121 14.8157L13.152 13.0657C13.3816 12.9331 13.5492 12.7147 13.6178 12.4585C13.6865 12.2024 13.6506 11.9294 13.518 11.6997L12.518 9.96775C12.3853 9.73807 12.1669 9.57048 11.9107 9.50185C11.6546 9.43321 11.3816 9.46914 11.152 9.60175L8.12095 11.3517C8.00719 11.4174 7.90746 11.5048 7.82747 11.6089C7.74749 11.7131 7.6888 11.832 7.65476 11.9588C7.62073 12.0857 7.61202 12.218 7.62912 12.3482C7.64623 12.4784 7.68881 12.604 7.75445 12.7177L8.75495 14.4497ZM9.62095 13.9497L9.37095 13.5167L9.80395 13.2667L10.054 13.6997L9.62095 13.9497ZM11.353 12.9497L10.92 13.1997L9.91995 11.4677L10.353 11.2177L11.353 12.9497ZM12.219 12.4497L11.969 12.0167L12.4025 11.7667L12.6525 12.1992L12.219 12.4497ZM11.9 10.8997L11.467 11.1497L11.217 10.7167L11.65 10.4667L11.9 10.8997ZM9.04995 11.9667L9.29995 12.3997L8.86745 12.6497L8.61745 12.2167L9.04995 11.9667Z"
        fill="black"
      />
      <path
        d="M29 22.0901V15.5001C28.9988 15.1027 28.8404 14.7218 28.5593 14.4408C28.2783 14.1597 27.8975 14.0013 27.5 14.0001H27.275C27.5446 13.7092 27.7401 13.3576 27.845 12.9751C28.0152 12.3352 27.9253 11.654 27.595 11.0801L22.595 2.42013C22.4317 2.13471 22.2132 1.88468 21.9522 1.68465C21.6913 1.48463 21.393 1.33861 21.075 1.25513C20.4351 1.08494 19.7539 1.17481 19.18 1.50513L5.32 9.50513C4.74582 9.83666 4.32685 10.3827 4.15525 11.0231C3.98366 11.6636 4.07349 12.3459 4.405 12.9201L5.03 14.0001H4.5C3.83696 14.0001 3.20107 14.2635 2.73223 14.7324C2.26339 15.2012 2 15.8371 2 16.5001V28.5001C2 29.1632 2.26339 29.7991 2.73223 30.2679C3.20107 30.7367 3.83696 31.0001 4.5 31.0001H27.5C27.8975 30.9989 28.2783 30.8405 28.5593 30.5595C28.8404 30.2784 28.9988 29.8976 29 29.5001V26.9101C29.2914 26.8063 29.5437 26.6153 29.7226 26.363C29.9016 26.1107 29.9984 25.8094 30 25.5001V23.5001C29.9984 23.1908 29.9016 22.8895 29.7226 22.6372C29.5437 22.3849 29.2914 22.1939 29 22.0901ZM5.12 11.2851C5.17085 11.0948 5.25889 10.9163 5.37903 10.7602C5.49917 10.604 5.64904 10.4731 5.82 10.3751L19.68 2.37013C19.8503 2.27164 20.0384 2.20774 20.2334 2.18209C20.4285 2.15643 20.6267 2.16953 20.8167 2.22062C21.0067 2.27172 21.1847 2.35981 21.3406 2.47985C21.4964 2.59988 21.6271 2.7495 21.725 2.92013L26.73 11.5801C26.8285 11.7504 26.8924 11.9385 26.918 12.1336C26.9437 12.3286 26.9306 12.5268 26.8795 12.7168C26.8284 12.9068 26.7403 13.0848 26.6203 13.2407C26.5002 13.3966 26.3506 13.5272 26.18 13.6251L22.07 16.0001H17.625C17.635 15.9018 17.6159 15.8027 17.57 15.7151L16.57 13.9801C16.5027 13.8652 16.3933 13.7809 16.265 13.7451C16.1365 13.7136 16.0009 13.7314 15.885 13.7951L14.15 14.7951C14.0353 14.862 13.9516 14.9715 13.9169 15.0997C13.8823 15.2279 13.8996 15.3646 13.965 15.4801L14.265 16.0001H7.34L5.27 12.4201C5.07191 12.0765 5.01797 11.6684 5.12 11.2851ZM16.45 15.7801L16.07 16.0001H15.42L15.08 15.4151L15.95 14.9151L16.45 15.7801ZM3.435 15.4401C3.71766 15.1581 4.10072 14.9999 4.5 15.0001H5.605L6.185 16.0001H4.5C4.36739 16.0001 4.24021 16.0528 4.14645 16.1466C4.05268 16.2403 4 16.3675 4 16.5001C4 16.6327 4.05268 16.7599 4.14645 16.8537C4.24021 16.9475 4.36739 17.0001 4.5 17.0001H26.5C26.6326 17.0001 26.7598 16.9475 26.8536 16.8537C26.9473 16.7599 27 16.6327 27 16.5001C27 16.3675 26.9473 16.2403 26.8536 16.1466C26.7598 16.0528 26.6326 16.0001 26.5 16.0001H24.07L25.8 15.0001H27.5C27.6326 15.0001 27.7598 15.0528 27.8536 15.1466C27.9473 15.2403 28 15.3675 28 15.5001V18.0001H4.5C4.20362 17.9997 3.914 17.9116 3.66758 17.7469C3.42115 17.5822 3.22893 17.3484 3.11509 17.0747C3.00125 16.8011 2.97088 16.4999 3.02781 16.2091C3.08473 15.9182 3.2264 15.6507 3.435 15.4401ZM28 29.5001C28 29.6327 27.9473 29.7599 27.8536 29.8537C27.7598 29.9475 27.6326 30.0001 27.5 30.0001H4.5C4.10254 29.9989 3.7217 29.8405 3.44065 29.5595C3.1596 29.2784 3.00119 28.8976 3 28.5001V18.4851C3.42951 18.8175 3.95688 18.9986 4.5 19.0001H28V22.0001H25.5C25.1025 22.0013 24.7217 22.1597 24.4407 22.4408C24.1596 22.7218 24.0012 23.1027 24 23.5001V25.5001C24.0012 25.8976 24.1596 26.2784 24.4407 26.5595C24.7217 26.8405 25.1025 26.9989 25.5 27.0001H28V29.5001ZM29 25.5001C29 25.6327 28.9473 25.7599 28.8536 25.8537C28.7598 25.9475 28.6326 26.0001 28.5 26.0001H25.5C25.3674 26.0001 25.2402 25.9475 25.1464 25.8537C25.0527 25.7599 25 25.6327 25 25.5001V23.5001C25 23.3675 25.0527 23.2403 25.1464 23.1466C25.2402 23.0528 25.3674 23.0001 25.5 23.0001H28.5C28.6326 23.0001 28.7598 23.0528 28.8536 23.1466C28.9473 23.2403 29 23.3675 29 23.5001V25.5001Z"
        fill="black"
      />
      <path
        d="M26.5 24H26C25.8674 24 25.7402 24.0527 25.6464 24.1464C25.5527 24.2402 25.5 24.3674 25.5 24.5C25.5 24.6326 25.5527 24.7598 25.6464 24.8536C25.7402 24.9473 25.8674 25 26 25H26.5C26.6326 25 26.7598 24.9473 26.8536 24.8536C26.9473 24.7598 27 24.6326 27 24.5C27 24.3674 26.9473 24.2402 26.8536 24.1464C26.7598 24.0527 26.6326 24 26.5 24Z"
        fill="black"
      />
      <path
        d="M6.5 28H5.5C5.36739 28 5.24021 27.9473 5.14645 27.8536C5.05268 27.7598 5 27.6326 5 27.5V26.5C5 26.3674 4.94732 26.2402 4.85355 26.1464C4.75979 26.0527 4.63261 26 4.5 26C4.36739 26 4.24021 26.0527 4.14645 26.1464C4.05268 26.2402 4 26.3674 4 26.5V27.5C4 27.8978 4.15804 28.2794 4.43934 28.5607C4.72064 28.842 5.10218 29 5.5 29H6.5C6.63261 29 6.75979 28.9473 6.85355 28.8536C6.94732 28.7598 7 28.6326 7 28.5C7 28.3674 6.94732 28.2402 6.85355 28.1464C6.75979 28.0527 6.63261 28 6.5 28Z"
        fill="black"
      />
    </svg>
  );

  const WithdrawalsIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.7325 8.56445C14.209 8.56445 12.9695 9.80314 12.9695 11.3257C12.9695 12.8493 14.209 14.0888 15.7325 14.0888C17.256 14.0888 18.4955 12.8493 18.4955 11.3257C18.4955 9.8032 17.256 8.56445 15.7325 8.56445ZM15.7325 13.1513C14.7259 13.1513 13.907 12.3324 13.907 11.3258C13.907 10.3201 14.7259 9.50202 15.7325 9.50202C16.739 9.50202 17.558 10.3201 17.558 11.3258C17.558 12.3323 16.739 13.1513 15.7325 13.1513Z"
        fill="black"
      />
      <path
        d="M27.6147 0H4.3853C3.19099 0 2.21936 0.971625 2.21936 2.166V9.55025C2.21936 10.7446 3.19099 11.7163 4.3853 11.7163H8.41111V14.8776C8.41111 15.1365 8.62099 15.3464 8.87986 15.3464C9.13874 15.3464 9.34861 15.1365 9.34861 14.8776V6.32688H10.7868C10.7865 8.47163 10.7853 17.2524 10.7844 17.2927C10.7801 17.4682 10.7689 17.9343 11.335 17.9837C11.3814 17.9877 11.4339 17.9923 11.509 18.0094C11.9278 18.1069 12.2594 18.4398 12.3542 18.8587C12.371 18.9323 12.3741 18.9789 12.3771 19.0241C12.4117 19.5463 12.8202 19.5784 13.0207 19.5784C13.0363 19.5784 17.1909 19.5771 17.1909 19.5771C17.3274 19.9093 17.539 20.2164 17.8244 20.4714L18.4341 21.0154H9.43792C9.38949 21.0154 9.34861 20.9737 9.34861 20.9242V17.0408C9.34861 16.7819 9.13874 16.5721 8.87986 16.5721C8.62099 16.5721 8.41111 16.7819 8.41111 17.0408V20.9242C8.41111 21.4914 8.87174 21.9529 9.43792 21.9529H14.7604C14.7973 22.9757 14.9674 24.2771 15.5018 25.391C15.7783 25.9673 16.1288 26.4357 16.5449 26.7922C16.117 26.9867 15.8182 27.4172 15.8182 27.917V30.7659C15.8182 31.4464 16.3726 32 17.054 32H20.1862C20.4451 32 20.655 31.7901 20.655 31.5312C20.655 31.2724 20.4451 31.0625 20.1862 31.0625H17.054C16.8923 31.0625 16.7557 30.9267 16.7557 30.7659V27.917C16.7557 27.7552 16.8923 27.6186 17.054 27.6186H25.4816C25.6434 27.6186 25.78 27.7552 25.78 27.917V30.7659C25.78 30.9266 25.6434 31.0625 25.4816 31.0625H22.3512C22.0924 31.0625 21.8825 31.2724 21.8825 31.5312C21.8825 31.7901 22.0924 32 22.3512 32H25.4817C26.1631 32 26.7175 31.4464 26.7175 30.7659V27.917C26.7175 27.3604 26.3475 26.8889 25.8407 26.7347L27.2105 19.6026C27.4903 18.1477 27.5809 16.3019 25.7787 14.9281L23.0539 12.9072V11.7163H27.6147C28.809 11.7163 29.7807 10.7446 29.7807 9.55025V2.166C29.7806 0.971625 28.809 0 27.6147 0ZM8.41111 8.403H5.5308V6.32688H8.41111V8.403ZM17.6275 17.0103C17.2113 17.4763 17.0069 18.0589 17.0067 18.6396H13.2657C13.0896 17.8808 12.487 17.2766 11.7243 17.097V6.32688H19.7423V16.2086C18.9753 16.1201 18.1784 16.3923 17.6275 17.0103ZM25.2152 15.6774C26.3274 16.5252 26.6387 17.6112 26.2897 19.4257L24.8962 26.6812H18.7928C16.2492 26.6068 15.7655 23.6511 15.6985 21.9529H19.4843C19.5917 22.0489 19.699 22.1448 19.8067 22.2411C20.0089 22.4199 20.3485 22.8898 20.4349 23.3302C20.4788 23.5537 20.6749 23.7087 20.8944 23.7087C20.9244 23.7087 20.9547 23.7059 20.9852 23.6999C21.2392 23.65 21.4048 23.4037 21.355 23.1496C21.226 22.4921 20.7839 21.8537 20.4299 21.5406C20.2799 21.4064 20.1295 21.2724 19.9793 21.1383C19.9729 21.1324 19.9667 21.1265 19.96 21.121C19.6858 20.8761 18.4489 19.7722 18.4489 19.7722C17.8259 19.2157 17.7711 18.2569 18.327 17.6344C18.8823 17.0116 19.8421 16.9564 20.466 17.5111L23.2244 19.972C23.4176 20.1443 23.7139 20.1274 23.8862 19.9342C24.0585 19.7411 24.0417 19.4447 23.8485 19.2724L23.0539 18.5635V14.0745L25.2152 15.6774ZM20.6799 16.5161V6.32688H22.1163V17.7271L21.0895 16.811C20.9609 16.6967 20.8235 16.5986 20.6799 16.5161ZM5.5308 5.38938V3.3115H26.4692V5.38938H5.5308ZM26.4692 6.32688V8.403H23.0538V6.32688H26.4692ZM28.8431 9.55025C28.8431 10.2276 28.292 10.7788 27.6147 10.7788H23.0538V9.34044H26.6438C27.0644 9.34044 27.4067 8.999 27.4067 8.57938V3.13512C27.4067 2.7155 27.0644 2.37406 26.6438 2.37406H5.35436C4.93474 2.37406 4.5933 2.7155 4.5933 3.13512V8.57944C4.5933 8.99906 4.93474 9.3405 5.35436 9.3405H8.41111V10.7788H4.3853C3.70792 10.7788 3.15686 10.2277 3.15686 9.55031V2.166C3.15686 1.48856 3.70792 0.9375 4.3853 0.9375H27.6147C28.292 0.9375 28.8431 1.48856 28.8431 2.166V9.55025Z"
        fill="black"
      />
    </svg>
  );

  const TransportationFeesIcon = () => (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.60751 6.2023L9.37501 7.5198C9.44419 7.62864 9.55275 7.70653 9.67801 7.73721C9.80327 7.76789 9.93554 7.74899 10.0472 7.68445C10.1588 7.61991 10.2412 7.51473 10.2772 7.39088C10.3131 7.26702 10.2998 7.13407 10.24 7.0198L9.22251 5.2698C9.15613 5.15586 9.04744 5.07276 8.92008 5.03857C8.79272 5.00439 8.65702 5.02191 8.54251 5.0873L6.79251 6.0873C6.67747 6.15361 6.59349 6.26289 6.55903 6.39112C6.52457 6.51935 6.54246 6.65602 6.60876 6.77105C6.67506 6.88609 6.78435 6.97008 6.91258 7.00454C7.04081 7.039 7.17747 7.02111 7.29251 6.9548L8.60751 6.2023Z"
        fill="black"
      />
      <path
        d="M10.86 24.2122C10.9264 24.3262 11.0351 24.4093 11.1624 24.4435C11.2898 24.4776 11.4255 24.4601 11.54 24.3947L13.29 23.3947C13.405 23.3284 13.489 23.2191 13.5235 23.0909C13.5579 22.9627 13.54 22.826 13.4737 22.711C13.4074 22.5959 13.2981 22.512 13.1699 22.4775C13.0417 22.443 12.905 22.4609 12.79 22.5272L11.475 23.2797L10.7075 21.9622C10.6383 21.8534 10.5297 21.7755 10.4045 21.7448C10.2792 21.7141 10.147 21.733 10.0353 21.7976C9.92365 21.8621 9.84125 21.9673 9.80532 22.0912C9.76939 22.215 9.7827 22.348 9.84248 22.4622L10.86 24.2122Z"
        fill="black"
      />
      <path
        d="M4.12743 7.62232L2.37743 8.62232C2.32035 8.65519 2.27032 8.69902 2.23023 8.75129C2.19014 8.80356 2.16077 8.86323 2.14381 8.92689C2.12685 8.99054 2.12263 9.05692 2.13141 9.1222C2.14018 9.18749 2.16177 9.2504 2.19493 9.30732L3.21243 11.0573C3.28162 11.1662 3.39018 11.244 3.51544 11.2747C3.64069 11.3054 3.77296 11.2865 3.88461 11.222C3.99626 11.1574 4.07866 11.0522 4.11459 10.9284C4.15052 10.8045 4.13721 10.6716 4.07743 10.5573L3.29993 9.24982L4.62243 8.49982C4.67939 8.46699 4.72933 8.42326 4.76939 8.37113C4.80945 8.319 4.83885 8.25949 4.85592 8.196C4.87298 8.13251 4.87737 8.06628 4.86884 8.00109C4.8603 7.9359 4.83901 7.87303 4.80618 7.81607C4.77335 7.75911 4.72962 7.70917 4.67749 7.66911C4.62537 7.62905 4.56586 7.59965 4.50236 7.58258C4.43887 7.56552 4.37264 7.56113 4.30745 7.56966C4.24226 7.5782 4.17939 7.59949 4.12243 7.63232L4.12743 7.62232Z"
        fill="black"
      />
      <path
        d="M11.855 12.4473C11.4635 12.1821 11.0145 12.0139 10.5451 11.9565C10.0757 11.8992 9.59945 11.9544 9.15562 12.1176C8.71178 12.2808 8.31316 12.5472 7.99267 12.8949C7.67219 13.2426 7.43906 13.6616 7.31254 14.1173C7.21885 14.4582 7.19404 14.8145 7.23954 15.1652C7.28504 15.5158 7.39994 15.854 7.57754 16.1597C7.78704 16.5205 8.07922 16.8264 8.43004 17.0522C8.54141 17.1227 8.67609 17.1463 8.80477 17.1179C8.93346 17.0896 9.04575 17.0116 9.11724 16.9009C9.18873 16.7902 9.21363 16.6558 9.18653 16.5268C9.15944 16.3979 9.08253 16.2848 8.97254 16.2122C8.75349 16.0722 8.57095 15.8819 8.44004 15.6573C8.32928 15.4654 8.25787 15.2534 8.22997 15.0336C8.20206 14.8138 8.21824 14.5907 8.27754 14.3772C8.36191 14.0769 8.51608 13.8007 8.72748 13.5712C8.93887 13.3417 9.2015 13.1655 9.49396 13.0568C9.78642 12.9481 10.1004 12.91 10.4104 12.9457C10.7203 12.9814 11.0175 13.0899 11.2775 13.2622C11.3603 13.3216 11.4373 13.3886 11.5075 13.4622C11.553 13.5097 11.6074 13.5477 11.6675 13.5741C11.7277 13.6006 11.7925 13.6149 11.8582 13.6163C11.9239 13.6177 11.9892 13.6061 12.0504 13.5823C12.1117 13.5584 12.1676 13.5227 12.215 13.4773C12.2625 13.4318 12.3005 13.3774 12.3269 13.3172C12.3534 13.2571 12.3677 13.1923 12.3691 13.1266C12.3705 13.0609 12.3589 12.9956 12.335 12.9344C12.3112 12.8731 12.2755 12.8172 12.23 12.7698C12.1153 12.6509 11.9897 12.5429 11.855 12.4473Z"
        fill="black"
      />
      <path
        d="M11.8875 4.89983C11.9685 4.89984 12.0483 4.88018 12.12 4.84255C12.1917 4.80492 12.2532 4.75045 12.2991 4.6838C12.3451 4.61716 12.3742 4.54034 12.384 4.45996C12.3937 4.37957 12.3838 4.29803 12.355 4.22233L11.635 2.33233C11.588 2.20834 11.4936 2.10813 11.3726 2.05374C11.2516 1.99936 11.114 1.99525 10.99 2.04233C10.8661 2.0894 10.7658 2.18381 10.7115 2.30477C10.6571 2.42573 10.653 2.56334 10.7 2.68733L11.42 4.57483C11.4557 4.67013 11.5195 4.7523 11.603 4.81037C11.6865 4.86844 11.7858 4.89965 11.8875 4.89983Z"
        fill="black"
      />
      <path
        d="M31.9225 27.0552C31.235 25.9777 25.185 16.5002 24.345 15.6777L24.17 15.5052C23.3775 14.7252 22.4825 13.8402 21.27 13.8402H21.25C20.3925 13.8402 18.64 13.9677 17.8725 14.0252L12.6575 0.322733C12.6104 0.199338 12.5164 0.0995826 12.396 0.0452485C12.2757 -0.00908564 12.1387 -0.013585 12.015 0.0327327L2.32249 3.67023C2.26087 3.69329 2.20441 3.72828 2.15633 3.77321C2.10826 3.81813 2.06953 3.8721 2.04236 3.93202C2.01519 3.99194 2.00011 4.05663 1.99798 4.12239C1.99586 4.18815 2.00674 4.25368 2.02999 4.31523L2.35999 5.18273C2.40707 5.30672 2.50147 5.40693 2.62243 5.46132C2.74339 5.5157 2.881 5.51981 3.00499 5.47273C3.12898 5.42566 3.22919 5.33125 3.28358 5.21029C3.33796 5.08933 3.34207 4.95172 3.29499 4.82773L3.14249 4.43023L11.8925 1.14273L17.8925 16.9227H17.8725C17.8639 16.8557 17.8418 16.791 17.8075 16.7327L9.89999 3.12273C9.83362 3.00879 9.72492 2.92568 9.59756 2.8915C9.4702 2.85732 9.3345 2.87484 9.21999 2.94023L0.249991 8.09023C0.192906 8.12311 0.14288 8.16694 0.102787 8.2192C0.0626936 8.27147 0.0333238 8.33115 0.016365 8.3948C-0.000593778 8.45845 -0.00480727 8.52483 0.00396664 8.59012C0.0127406 8.65541 0.0343289 8.71831 0.0674913 8.77523L10.28 26.3027C10.2949 26.3257 10.3116 26.3475 10.33 26.3677C10.3401 26.3816 10.351 26.3949 10.3625 26.4077C10.3805 26.4241 10.3998 26.4392 10.42 26.4527C10.4517 26.4821 10.4904 26.5028 10.5325 26.5127C10.5883 26.5404 10.6503 26.5533 10.7125 26.5502C10.7556 26.5499 10.7984 26.544 10.84 26.5327L10.8775 26.5177C10.906 26.5086 10.9336 26.4968 10.96 26.4827L13.115 25.2327C13.0416 25.5809 13.0771 25.9433 13.2167 26.2706C13.3562 26.598 13.593 26.8745 13.895 27.0627C14.265 27.3127 17.305 28.9202 18.1775 28.9202C18.72 28.9202 19.785 28.4602 20.405 28.1702C20.9339 29.152 21.6736 30.0046 22.5709 30.6667C23.4682 31.3289 24.5009 31.7843 25.595 32.0002H25.6675C25.7332 32.005 25.7991 31.9968 25.8616 31.976C25.9241 31.9553 25.9819 31.9225 26.0317 31.8794C26.0815 31.8364 26.1223 31.7839 26.1518 31.7251C26.1813 31.6662 26.199 31.6021 26.2037 31.5365C26.2085 31.4708 26.2003 31.4049 26.1796 31.3424C26.1588 31.2799 26.126 31.2221 26.0829 31.1723C26.0399 31.1225 25.9874 31.0817 25.9286 31.0522C25.8697 31.0226 25.8057 31.005 25.74 31.0002C24.7871 30.8087 23.8889 30.4073 23.1105 29.8252C22.3321 29.2432 21.6932 28.4951 21.24 27.6352C21.0448 27.2406 20.8729 26.8349 20.725 26.4202C20.37 25.4927 20.005 24.5352 19.375 23.9877C19.0425 23.6977 17.82 22.7377 16.4075 21.6177L14.3875 20.0177C13.6925 19.4677 11.58 17.7902 11.1725 17.3902C11.0266 17.2654 10.9072 17.1126 10.8213 16.9408C10.7354 16.7691 10.6848 16.5819 10.6725 16.3902C10.7041 16.0049 10.8832 15.6467 11.1725 15.3902C11.595 14.9627 12.2525 14.9877 13.1225 15.4602L21.5 20.0002C21.5722 20.0392 21.6529 20.0598 21.735 20.0602C21.8474 20.0631 21.9575 20.028 22.0475 19.9607C22.1374 19.8933 22.2021 19.7976 22.231 19.6889C22.2599 19.5803 22.2513 19.465 22.2067 19.3619C22.1621 19.2587 22.084 19.1736 21.985 19.1202L19.25 17.6452C19.2447 17.618 19.2372 17.5912 19.2275 17.5652L18.25 15.0002C19.0975 14.9402 20.54 14.8452 21.2625 14.8402C22.0625 14.8402 22.7425 15.5102 23.46 16.2177L23.6375 16.3927C24.2125 16.9552 28.3875 23.3927 31.07 27.5927C31.1041 27.6506 31.1495 27.701 31.2034 27.741C31.2574 27.781 31.3189 27.8096 31.3842 27.8253C31.4496 27.841 31.5174 27.8433 31.5836 27.8322C31.6499 27.8211 31.7132 27.7968 31.7698 27.7606C31.8264 27.7245 31.8752 27.6773 31.9132 27.6219C31.9512 27.5666 31.9776 27.5041 31.991 27.4382C32.0043 27.3724 32.0042 27.3045 31.9907 27.2387C31.9772 27.1729 31.9506 27.1105 31.9125 27.0552H31.9225ZM19.5125 26.0552C19.6125 26.3052 19.71 26.5552 19.8025 26.7852C19.8675 26.9552 19.93 27.1177 19.995 27.2852C19.2575 27.6277 18.4475 27.9427 18.1775 27.9452C17.6975 27.9327 15.0525 26.6477 14.4425 26.2502C14.2758 26.1519 14.1536 25.9929 14.1014 25.8065C14.0492 25.6202 14.0711 25.4208 14.1625 25.2502C14.207 25.1338 14.2768 25.0286 14.3666 24.9421C14.4565 24.8557 14.5644 24.7902 14.6825 24.7502C15.0175 25.0002 15.4075 25.2502 15.7675 25.4702C16.06 25.6527 16.335 25.8252 16.4875 25.9427C16.7575 26.1552 17.255 26.2227 17.7775 26.2227C18.3598 26.217 18.9405 26.1593 19.5125 26.0502V26.0552ZM19.0475 25.1327C18.45 25.2277 17.345 25.3052 17.1025 25.1677C16.9075 25.0152 16.625 24.8402 16.2975 24.6327C15.97 24.4252 15.615 24.2052 15.3175 23.9852L16.75 23.1602C17.725 23.9302 18.5 24.5377 18.73 24.7502C18.8533 24.8619 18.9609 24.9897 19.05 25.1302L19.0475 25.1327ZM10.46 14.6952C10.2204 14.9147 10.0273 15.18 9.892 15.4755C9.75673 15.7709 9.68209 16.0905 9.67249 16.4152C9.68618 16.738 9.76525 17.0547 9.90491 17.3461C10.0446 17.6374 10.2419 17.8974 10.485 18.1102C10.7925 18.4102 11.8075 19.2427 13.7775 20.8052L15.8 22.4052L15.91 22.4902L10.8925 25.3727L1.18499 8.70773L9.28749 4.05523L16.265 16.0327L13.5975 14.5877C11.9675 13.7052 11 14.1577 10.46 14.6952Z"
        fill="black"
      />
    </svg>
  );

  // Raw Net Balance drives the Pay In / Pay Out cards (same logic as the pharmacy dashboard):
  //   • positive → Tiryaq owes the center → Pay In (payment for the center) → green
  //   • negative → the center owes Tiryaq → Pay Out (payment for Tiryaq)     → red
  const netBalanceRaw = Number(earningsMetrics?.netBalance || 0);
  // Pay In / Pay Out cards show ACTUAL completed settlement amounts (same as the
  // doctor dashboard): Pay In = completed Pay-Out settlements (Tiryaq → center) held
  // in `withdrawals`; Pay Out = completed Pay-In settlements (center → Tiryaq).
  const withdrawalsRaw = Number(earningsMetrics?.withdrawals || 0);
  const completedPayInsRaw = Number(earningsMetrics?.completedPayIns || 0);

  const dashboardStats = [
    {
      id: 1,
      icon: TotalEarningsIcon,
      label: "Total Earnings",
      value: earningsMetrics?.totalEarnings?.toLocaleString() || "0",
    },
    {
      id: 2,
      icon: CodIcon,
      label: "COD (Cash on Delivery)",
      value: earningsMetrics?.codCashOnDelivery?.toLocaleString() || "0",
    },
    {
      id: 3,
      icon: OnlinePaymentIcon,
      label: "Online Payments",
      value: earningsMetrics?.onlinePayments?.toLocaleString() || "0",
    },
    {
      id: 4,
      icon: AdminFeesIcon,
      label: "Admin Fees",
      value: earningsMetrics?.adminFees?.toLocaleString() || "0",
    },
    {
      id: 5,
      icon: CommissionIcon,
      label: "Commission",
      value: earningsMetrics?.commission?.toLocaleString() || "0",
    },
    {
      id: 6,
      icon: NetBalanceIcon,
      label: "Net Balance",
      value: earningsMetrics?.netBalance?.toLocaleString() || "0",
      color: netBalanceRaw > 0 ? "#16a34a" : netBalanceRaw < 0 ? "#dc2626" : undefined,
    },
    {
      id: 9,
      icon: NetBalanceIcon,
      label: "Pay In (Payment for the Center)",
      value: withdrawalsRaw.toLocaleString(),
      color: withdrawalsRaw > 0 ? "#16a34a" : undefined,
    },
    {
      id: 10,
      icon: WithdrawalsIcon,
      label: "Pay Out (Payment for Tiryaq)",
      value: completedPayInsRaw.toLocaleString(),
      color: completedPayInsRaw > 0 ? "#16a34a" : undefined,
    },
    // Withdrawals card removed: a withdrawal request is represented by the
    // Pay In (Payment for the Center) amount, so a separate Withdrawals metric
    // card is redundant on the Metrics section.
    {
      id: 8,
      icon: TransportationFeesIcon,
      label: "Transportation Fees",
      value: earningsMetrics?.transportationFees?.toLocaleString() || "0",
    },
    {
      id: 12,
      icon: TotalEarningsIcon,
      label: "Loyalty Point & Marketing Promotion",
      // Loyalty / promotion discount patients redeemed on this centre's bookings, reimbursed
      // to the centre by the platform and therefore part of Total Earnings:
      //   Total Earnings = COD + Online + Loyalty & Promotion
      // (Physiotherapy has no insurance, so there is no coverage term.) This page reads the
      // SAME metrics endpoint as the dashboard, so the card must exist here too — otherwise
      // Total Earnings would include an amount with no line item accounting for it.
      value: earningsMetrics?.loyaltyPromotion?.toLocaleString() || "0",
    },
  ];

  // Map API earnings data to the table format
  const ordersTableData = (earningsListData || []).map((item) => {
    // Format date
    let serviceDate = '—';
    if (item.serviceDate) {
      try {
        const d = new Date(item.serviceDate);
        serviceDate = `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
      } catch (e) { serviceDate = '—'; }
    }

    // Format time
    let serviceTime = '—';
    if (item.serviceTime) {
      try {
        const parts = item.serviceTime.split(':');
        const hour = parseInt(parts[0]);
        const minute = parts[1] || '00';
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        serviceTime = `${String(displayHour).padStart(2, '0')}:${minute} ${period}`;
      } catch (e) { serviceTime = '—'; }
    }

    // Format order status for display
    const orderStatusDisplay = item.orderStatus
      ? item.orderStatus.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
      : '';

    // Format payment method. A null/empty method means the patient hasn't paid
    // yet, so show a "Not paid yet" hint instead of a blank cell.
    let paymentMethod = item.paymentMethod || '';
    if (paymentMethod === 'COD') paymentMethod = 'Cash on Delivery';
    else if (paymentMethod === 'WALLET') paymentMethod = 'Wallet';
    else if (paymentMethod === 'CARD') paymentMethod = 'Card';
    else if (!paymentMethod) paymentMethod = 'Not paid yet';

    // Format payment status — default to "Pending" (payment not completed yet)
    // instead of a blank cell.
    let paymentStatus = 'Pending';
    if (item.paymentStatus === 'COMPLETED' || item.paymentStatus === 'PAID') {
      paymentStatus = 'Payment Complete';
    } else if (item.paymentMethod === 'COD' && (item.orderStatus === 'ORDER_ACCEPTED' || item.orderStatus === 'COMPLETED')) {
      paymentStatus = 'COD';
    }

    return {
      id: item.bookingNumber || `PT-${item.id}`,
      bookingId: item.id,
      name: item.patientName || 'Patient',
      avatar: item.patientAvatarPresignedUrl || null,
      serviceDate,
      serviceTime,
      serviceName: item.serviceName || '—',
      paymentMethod,
      orderStatus: orderStatusDisplay,
      paymentStatus,
      totalAmount: Number(item.totalAmount || 0),
      adminFeeAmount: Number(item.adminFeeAmount || 0),
      transportationFee: Number(item.transportationFee || 0),
      netAmount: Math.round(Number(item.netAmount || (Number(item.totalAmount || 0) - Number(item.adminFeeAmount || 0)))),
    };
  });


  const therapistPayoutTableData = [];

  return (
    <PhysioDashboardLayout
      currentPage="physio_earning_payment"
      onNavigate={handleNavigate}
      onLogout={onLogout}
    >
      {/* Front-face service-name tooltip (position:fixed so it is never clipped
          by the earnings table's horizontal scroll box). */}
      {svcTip && (
        <div
          className={styles["testNamesList"]}
          style={{
            position: "fixed",
            left: svcTip.x,
            top: svcTip.y,
            display: "block",
            zIndex: 99999,
            pointerEvents: "none",
            transform: svcTip.flipUp ? "translateY(-100%)" : "none",
          }}
        >
          <ul>
            <li><span>Services</span></li>
            {svcTip.items.map((svc, i) => (
              <li key={i}>{svc}</li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles["apt-page-header"]} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px" }}>
        <h1>Earning and Payments</h1>
        {SHOW_INITIATE_WITHDRAWAL && (
        <Link to="/physio_withdraw_from_wallet">
          <button type="button" className={styles["wallet-withdraw-btn"]}>
            <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask id="mask0_physio_withdraw" maskUnits="userSpaceOnUse" x="0" y="0" width="21" height="20">
                <path d="M20.2852 0H0.285156V20H20.2852V0Z" fill="white" />
              </mask>
              <g mask="url(#mask0_physio_withdraw)">
                <path d="M6.26064 18.9577C4.27731 18.9577 2.65234 17.516 2.65234 15.741V14.041C2.65234 13.6993 2.93568 13.416 3.27734 13.416C3.61901 13.416 3.90234 13.6993 3.90234 14.041C3.90234 15.0827 4.91064 15.866 6.26064 15.866C7.61064 15.866 8.61899 15.0827 8.61899 14.041C8.61899 13.6993 8.90231 13.416 9.24398 13.416C9.58564 13.416 9.86898 13.6993 9.86898 14.041V15.741C9.86898 17.516 8.25231 18.9577 6.26064 18.9577ZM4.11897 16.5577C4.48564 17.241 5.31064 17.7077 6.26064 17.7077C7.21064 17.7077 8.03565 17.2327 8.40232 16.5577C7.81065 16.916 7.07731 17.1244 6.26064 17.1244C5.44398 17.1244 4.71064 16.916 4.11897 16.5577Z" fill="#1F1F1F" />
                <path d="M6.26064 14.8328C4.89398 14.8328 3.669 14.2078 3.06067 13.2162C2.794 12.7828 2.65234 12.2745 2.65234 11.7578C2.65234 10.8828 3.03567 10.0662 3.73567 9.45784C5.08567 8.27453 7.41065 8.27457 8.76898 9.44959C9.46898 10.0663 9.86064 10.8828 9.86064 11.7578C9.86064 12.2745 9.71898 12.7828 9.45231 13.2162C8.85231 14.2078 7.62731 14.8328 6.26064 14.8328ZM6.26064 9.79125C5.61064 9.79125 5.01066 10.0078 4.56066 10.3995C4.13566 10.7662 3.90234 11.2495 3.90234 11.7578C3.90234 12.0495 3.97731 12.3163 4.12731 12.5663C4.51064 13.1996 5.32731 13.5913 6.26064 13.5913C7.19398 13.5913 8.01064 13.1996 8.38564 12.5746C8.53564 12.3329 8.61065 12.0579 8.61065 11.7663C8.61065 11.2579 8.37734 10.7745 7.95234 10.3995C7.51067 10.0078 6.91064 9.79125 6.26064 9.79125Z" fill="#2D2D37" />
                <path d="M6.26064 17.1248C4.20231 17.1248 2.65234 15.7998 2.65234 14.0498V11.7582C2.65234 9.98317 4.26898 8.5415 6.26064 8.5415C7.20231 8.5415 8.10233 8.8665 8.77731 9.44984C9.47731 10.0665 9.86898 10.8832 9.86898 11.7582V14.0498C9.86898 15.7998 8.31898 17.1248 6.26064 17.1248ZM6.26064 9.7915C4.96064 9.7915 3.90234 10.6748 3.90234 11.7582V14.0498C3.90234 15.0915 4.91064 15.8748 6.26064 15.8748C7.61064 15.8748 8.61899 15.0915 8.61899 14.0498V11.7582C8.61899 11.2498 8.38568 10.7665 7.96068 10.3915C7.51068 10.0082 6.91064 9.7915 6.26064 9.7915Z" fill="#2D2D37" />
                <path d="M16.1544 12.3332C14.896 12.3332 13.8294 11.3998 13.7294 10.1998C13.6627 9.50817 13.9127 8.83317 14.4127 8.3415C14.8294 7.90816 15.421 7.6665 16.046 7.6665H17.7877C18.6127 7.6915 19.246 8.3415 19.246 9.1415V10.8582C19.246 11.6582 18.6127 12.3082 17.8127 12.3332H16.1544ZM17.7627 8.9165H16.0544C15.7627 8.9165 15.496 9.02484 15.3044 9.22484C15.0627 9.45817 14.946 9.77484 14.9794 10.0915C15.021 10.6415 15.5544 11.0832 16.1544 11.0832H17.7877C17.896 11.0832 17.996 10.9832 17.996 10.8582V9.1415C17.996 9.0165 17.896 8.92484 17.7627 8.9165Z" fill="#2D2D37" />
                <path d="M13.6203 17.7082H11.537C11.1953 17.7082 10.912 17.4248 10.912 17.0832C10.912 16.7415 11.1953 16.4582 11.537 16.4582H13.6203C15.7703 16.4582 17.162 15.0665 17.162 12.9165V12.3332H16.1536C14.8953 12.3332 13.8286 11.3998 13.7286 10.1998C13.662 9.50817 13.912 8.83317 14.412 8.3415C14.8287 7.90818 15.4203 7.66651 16.0453 7.66651H17.1536V7.08317C17.1536 5.13317 16.012 3.79149 14.162 3.57482C13.962 3.54149 13.787 3.5415 13.612 3.5415H6.11198C5.91198 3.5415 5.7203 3.55816 5.52864 3.58316C3.6953 3.8165 2.57031 5.14984 2.57031 7.08317V8.74984C2.57031 9.0915 2.28698 9.37484 1.94531 9.37484C1.60365 9.37484 1.32031 9.0915 1.32031 8.74984V7.08317C1.32031 4.5165 2.90368 2.65818 5.36201 2.34985C5.58701 2.31651 5.84531 2.2915 6.11198 2.2915H13.612C13.812 2.2915 14.0703 2.29984 14.337 2.3415C16.7953 2.62484 18.4036 4.4915 18.4036 7.08317V8.29151C18.4036 8.63317 18.1203 8.9165 17.7786 8.9165H16.0453C15.7536 8.9165 15.487 9.02484 15.2954 9.22484C15.0537 9.45817 14.937 9.77484 14.9703 10.0915C15.012 10.6415 15.5454 11.0832 16.1454 11.0832H17.787C18.1286 11.0832 18.412 11.3665 18.412 11.7082V12.9165C18.412 15.7832 16.487 17.7082 13.6203 17.7082Z" fill="#2D2D37" />
              </g>
            </svg>
            {t("wallet.balance.withdraw_button", "Initiate Withdrawal")}
          </button>
        </Link>
        )}
      </div>

        {/* Metrics label + From/To date-range picker — same design as the lab / imaging /
            pharmacy pages. Scopes every metric card below to the selected period. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
            marginBottom: "1rem",
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 500, color: "#099494" }}>
            {t("physio.earningPayment.metrics", "Metrics")}
          </span>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <div className="ins-metrics-datepicker">
              <DatePicker
                selected={metricsDateFrom}
                onChange={(d) => setMetricsDateFrom(d)}
                selectsStart
                startDate={metricsDateFrom}
                endDate={metricsDateTo}
                maxDate={metricsDateTo || undefined}
                dateFormat="MM/dd/yyyy"
                placeholderText={t(
                  "physio.earningPayment.filter.fromPlaceholder",
                  "From date",
                )}
                isClearable
              />
            </div>
            <div className="ins-metrics-datepicker">
              <DatePicker
                selected={metricsDateTo}
                onChange={(d) => setMetricsDateTo(d)}
                selectsEnd
                startDate={metricsDateFrom}
                endDate={metricsDateTo}
                minDate={metricsDateFrom || undefined}
                dateFormat="MM/dd/yyyy"
                placeholderText={t(
                  "physio.earningPayment.filter.toPlaceholder",
                  "To date",
                )}
                isClearable
              />
            </div>
            {(metricsDateFrom || metricsDateTo) && (
              <button
                type="button"
                onClick={() => {
                  setMetricsDateFrom(null);
                  setMetricsDateTo(null);
                }}
                style={{
                  padding: "9px 14px",
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  fontSize: 14,
                  background: "#fff",
                  cursor: "pointer",
                  color: "#374151",
                }}
              >
                {t("physio.earningPayment.filter.clear", "Clear")}
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className={styles["db-stats-grid"]}>
          {dashboardStats.map((item) => {
            return (
              <div className={`${styles["db-lab-card"]}`} key={item.id}>
                <div className={`${styles["db-lab-cardIcon"]}`}>
                  <item.icon />
                </div>
                <div className={`${styles["db-lab-cardText"]}`}>
                  <p>{item.label}</p>
                  <h3 style={item.color ? { color: item.color } : undefined}>
                    <small>IQD</small>
                    {item.value}
                  </h3>
                </div>
              </div>
            );
          })}
        </div>
      <div className={styles["apt-appointments-section"]}>
        <div className={styles["apt-tabButtons-wrapper"]}>
          <button
            className={`${styles["tabButton"]} ${activeTab === "Earnings" ? styles["active"] : ""}`}
            onClick={() => setActiveTab("Earnings")}
          >
            Earnings
          </button>
          <button
            className={`${styles["tabButton"]} ${activeTab === "Payouts" ? styles["active"] : ""}`}
            onClick={() => setActiveTab("Payouts")}
          >
            Payouts
          </button>
        </div>
        {/* <div className={styles["apt-section-header"]}>
          <h2>Earnings</h2>
          <div className={styles["apt-top-actions"]}>
              <button className={styles["exportBtn"]}>
                <FiDownload /> Export
              </button>
            </div>
        </div> */}

        
        {activeTab === "Earnings" && (
          <>
            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                {/* <span className={styles["apt-filter-icon-wrap"]}>
                <Filter className={styles["apt-filter-icon"]} />
              </span> */}
                <div className={styles["apt-search-wrapper"]}>
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder="Search appointment id or patient..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles["apt-search-input"]}
                  />
                </div>
                <select className={styles["apt-status-filter"]}>
                  <option value="">Select Service</option>
                  <option value="All">
                    {t("dashboard.appointments.table.status")}
                  </option>
                  <option value="Pending">
                    {t("appointments.filters.status.pending")}
                  </option>
                  <option value="Upcoming">
                    {t("appointments.filters.status.upcoming")}
                  </option>
                  <option value="Ongoing">
                    {t("appointments.filters.status.ongoing")}
                  </option>
                  <option value="Completed">
                    {t("appointments.filters.status.completed")}
                  </option>
                  <option value="Cancelled">
                    {t("appointments.filters.status.cancelled")}
                  </option>
                  <option value="Reschedule Requested">
                    {t("dashboard.appointments.tab.rescheduleRequested")}
                  </option>
                </select>
                <select className={styles["apt-status-filter"]}>
                  <option value="">Payment Status</option>
                  <option value="All">
                    {t("dashboard.appointments.table.status")}
                  </option>
                  <option value="Pending">
                    {t("appointments.filters.status.pending")}
                  </option>
                  <option value="Upcoming">
                    {t("appointments.filters.status.upcoming")}
                  </option>
                  <option value="Ongoing">
                    {t("appointments.filters.status.ongoing")}
                  </option>
                  <option value="Completed">
                    {t("appointments.filters.status.completed")}
                  </option>
                  <option value="Cancelled">
                    {t("appointments.filters.status.cancelled")}
                  </option>
                  <option value="Reschedule Requested">
                    {t("dashboard.appointments.tab.rescheduleRequested")}
                  </option>
                </select>
                <select className={styles["apt-status-filter"]}>
                  <option value="">Order Status</option>
                  <option value="All">
                    {t("dashboard.appointments.table.status")}
                  </option>
                  <option value="Pending">
                    {t("appointments.filters.status.pending")}
                  </option>
                  <option value="Upcoming">
                    {t("appointments.filters.status.upcoming")}
                  </option>
                  <option value="Ongoing">
                    {t("appointments.filters.status.ongoing")}
                  </option>
                  <option value="Completed">
                    {t("appointments.filters.status.completed")}
                  </option>
                  <option value="Cancelled">
                    {t("appointments.filters.status.cancelled")}
                  </option>
                  <option value="Reschedule Requested">
                    {t("dashboard.appointments.tab.rescheduleRequested")}
                  </option>
                </select>
                <div className={styles["date-wrapper"]}>
                  <DatePicker
                    key={`datepicker-${date ? date.getTime() : "empty"}`}
                    className={styles["apt-date-filter"]}
                    selected={date}
                    onChange={(d) => setDate(d)}
                    placeholderText="Select Date"
                  />
                </div>
                {/* <button className={styles["apt-apply-btn"]}>{t('appointments.filters.button.apply')}</button> */}
                {/* <button
                className={styles["apt-apply-btn"]}
                onClick={() => {
                  setCurrentPage(1);
                  fetchAppointmentsWithFilters(1);
                }}
              >
                {t("appointments.filters.button.apply")}
              </button> */}
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={resetFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>
            {/* Appointments Table */}
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Service Date</th>
                    <th>Service Time</th>
                    <th>Service Name</th>
                    <th>Payment Method</th>
                    <th>Net Amount</th>
                    <th>Order Status</th>
                    <th>Payment Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {ordersTableData.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '40px 20px', color: '#89899b' }}>
                        No records found
                      </td>
                    </tr>
                  ) : ordersTableData.map((item, index) => (
                    <tr key={index}>
                      <td>{item.id}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>{item.name}</span>
                        </div>
                      </td>
                      <td>{item.serviceDate}</td>
                      <td>{item.serviceTime}</td>
                      <td className={styles.wrapText}>{item.serviceName && item.serviceName.length > 20 ? (<div className={styles["testNames"]}><div className={styles["truncateTestNames"]}>{item.serviceName}</div><div className={styles["testNamesList"]}><ul><li><span>Services</span></li>{item.serviceName.split(",").map((svc, i) => (<li key={i}>{svc.trim()}</li>))}</ul></div></div>) : (item.serviceName || '—')}</td>
                      <td>
                        {item.paymentMethod === "Not paid yet" ? (
                          <span style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic" }}>
                            Not paid yet
                          </span>
                        ) : (
                          item.paymentMethod
                        )}
                      </td>
                      <td style={{ fontWeight: 600, color: '#099494' }}>IQD {item.netAmount.toLocaleString()}</td>

                      <td>
                        <span
                          className={`${styles["db-status"]} ${styles[getStatusColor(item.orderStatus)]}`}
                        >
                          {item.orderStatus}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`${styles["db-status"]} ${styles[getStatusColor(item.paymentStatus)]}`}
                        >
                          {item.paymentStatus}
                        </span>
                      </td>

                      <td>
                        <div className={styles["apt-action-buttons"]}>
                          <Link to={`/physio_earning_detail?id=${item.bookingId}`}>
                            <button className={styles["apt-view-btn"]} title="View Details">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 8.25C11.0054 8.25 10.0516 8.64509 9.34835 9.34835C8.64509 10.0516 8.25 11.0054 8.25 12C8.25 12.9946 8.64509 13.9484 9.34835 14.6516C10.0516 15.3549 11.0054 15.75 12 15.75C12.9946 15.75 13.9484 15.3549 14.6516 14.6516C15.3549 13.9484 15.75 12.9946 15.75 12C15.75 11.0054 15.3549 10.0516 14.6516 9.34835C13.9484 8.64509 12.9946 8.25 12 8.25ZM9.75 12C9.75 11.4033 9.98705 10.831 10.409 10.409C10.831 9.98705 11.4033 9.75 12 9.75C12.5967 9.75 13.169 9.98705 13.591 10.409C14.0129 10.831 14.25 11.4033 14.25 12C14.25 12.5967 14.0129 13.169 13.591 13.591C13.169 14.0129 12.5967 14.25 12 14.25C11.4033 14.25 10.831 14.0129 10.409 13.591C9.98705 13.169 9.75 12.5967 9.75 12Z" fill="#155DFC"/>
                                <path fillRule="evenodd" clipRule="evenodd" d="M12 3.25C7.486 3.25 4.445 5.954 2.68 8.247L2.649 8.288C2.249 8.807 1.882 9.284 1.633 9.848C1.366 10.453 1.25 11.112 1.25 12C1.25 12.888 1.366 13.547 1.633 14.152C1.883 14.716 2.25 15.194 2.649 15.712L2.681 15.753C4.445 18.046 7.486 20.75 12 20.75C16.514 20.75 19.555 18.046 21.32 15.753L21.351 15.712C21.751 15.194 22.118 14.716 22.367 14.152C22.634 13.547 22.75 12.888 22.75 12C22.75 11.112 22.634 10.453 22.367 9.848C22.117 9.284 21.75 8.807 21.351 8.288L21.319 8.247C19.555 5.954 16.514 3.25 12 3.25ZM3.87 9.162C5.498 7.045 8.15 4.75 12 4.75C15.85 4.75 18.501 7.045 20.13 9.162C20.57 9.732 20.826 10.072 20.995 10.454C21.153 10.812 21.25 11.249 21.25 12C21.25 12.751 21.153 13.188 20.995 13.546C20.826 13.928 20.569 14.268 20.131 14.838C18.5 16.955 15.85 19.25 12 19.25C8.15 19.25 5.499 16.955 3.87 14.838C3.43 14.268 3.174 13.928 3.005 13.546C2.847 13.188 2.75 12.751 2.75 12C2.75 11.249 2.847 10.812 3.005 10.454C3.174 10.072 3.432 9.732 3.87 9.162Z" fill="#155DFC"/>
                              </svg>
                            </button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === "Payouts" && (
          <>
            {/* Filters */}
            <div className={styles["apt-filters"]}>
              <div className={styles["apt-filter-group"]}>
                <div className={styles["apt-search-wrapper"]}>
                  <Search className={styles["apt-search-icon"]} />
                  <input
                    type="text"
                    placeholder="Search reference or amount..."
                    value={withdrawalSearch}
                    onChange={(e) => setWithdrawalSearch(e.target.value)}
                    className={styles["apt-search-input"]}
                  />
                </div>
                <select
                  className={styles["apt-status-filter"]}
                  value={withdrawalStatusFilter}
                  onChange={(e) => setWithdrawalStatusFilter(e.target.value)}
                >
                  <option value="">Request Status</option>
                  <option value="ALL">All Status</option>
                  <option value="pending">Requested</option>
                  <option value="approved">Processing</option>
                  <option value="completed">Completed</option>
                  <option value="rejected">Failed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                <select
                  className={styles["apt-status-filter"]}
                  value={withdrawalMethodFilter}
                  onChange={(e) => setWithdrawalMethodFilter(e.target.value)}
                >
                  <option value="">Payment Method</option>
                  <option value="ALL">All Methods</option>
                  <option value="zain_cash">Zain Cash</option>
                  <option value="qi_card">Qi Card</option>
                  <option value="wire_transfer">Wire Transfer</option>
                  <option value="pay_billing">PayBilling</option>
                </select>
                <div className={styles["date-wrapper"]}>
                  <DatePicker
                    key={`wd-datepicker-${withdrawalDate ? withdrawalDate.getTime() : "empty"}`}
                    className={styles["apt-date-filter"]}
                    selected={withdrawalDate}
                    onChange={(d) => setWithdrawalDate(d)}
                    placeholderText="Select Date"
                  />
                </div>
                <button
                  className={styles["apt-reset-btn"]}
                  onClick={clearWithdrawalFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>

            {/* Withdrawals Table */}
            <div className={styles["apt-appointments-table"]}>
              <table>
                <thead>
                  <tr>
                    <th>Date Requested</th>
                    <th>Type</th>
                    <th>Bank Account</th>
                    <th>Amount Withdrawn</th>
                    <th>Remaining Balance</th>
                    <th>Request Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loadingWithdrawals ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>
                        <Loader />
                      </td>
                    </tr>
                  ) : settlementRecords.length === 0 ? (
                    <tr>
                      <td colSpan="6" style={{ textAlign: "center", padding: "40px 20px", color: "#89899b" }}>
                        No withdrawal records found
                      </td>
                    </tr>
                  ) : (
                    settlementRecords.map((s) => {
                      const label = withdrawalStatusLabel(s.status);
                      // s.net_balance is the balance snapshot frozen at request time (BEFORE
                      // this withdrawal). "Remaining Balance" should reflect what is left AFTER
                      // the withdrawal, so deduct the settled amount. A Pay-In moves the
                      // (negative) balance toward zero instead.
                      const remainingBalance =
                        s.direction === "pay_in"
                          ? Number(s.net_balance || 0) + Number(s.settlement_amount || 0)
                          : Number(s.net_balance || 0) - Number(s.settlement_amount || 0);
                      return (
                        <tr key={s.id}>
                          <td>{s.created_at ? tzDateNumeric(new Date(s.created_at)) : "—"}</td>
                          {/* Direction: Pay Out = Tiryaq → center (a withdrawal); Pay In = center → Tiryaq. */}
                          <td>
                            {/* `direction` is stored from the admin's view; invert it for the
                                center: admin PAY_IN = center Pay Out, admin PAY_OUT = center Pay In. */}
                            <span style={{ fontWeight: 600, color: s.direction === "pay_in" ? "#EC003F" : "#16a34a" }}>
                              {s.direction === "pay_in" ? "Pay Out" : "Pay In"}
                            </span>
                          </td>
                          <td>{withdrawalAccountLabel(s.account_details)}</td>
                          <td style={{ color: s.direction === "pay_in" ? "#EC003F" : "#16a34a" }}>{s.direction === "pay_in" ? "- " : ""}IQD {formatWithdrawAmount(s.settlement_amount)}</td>
                          <td>IQD {formatWithdrawAmount(remainingBalance)}</td>
                          <td>
                            <span className={`${styles["db-status"]} ${styles[withdrawalStatusClass(s.status)]}`}>
                              {label}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Withdrawals Pagination */}
            <div className={styles["apt-pagination"]}>
              <div className={styles["apt-pagination-info"]}>
                {t("dashboard.pagination.showing")}{" "}
                {withdrawalsTotal === 0 ? 0 : (withdrawalsPage - 1) * itemsPerPage + 1}{" "}
                {t("dashboard.pagination.to")}{" "}
                {Math.min(withdrawalsPage * itemsPerPage, withdrawalsTotal)}{" "}
                {t("dashboard.pagination.of")} {withdrawalsTotal}
              </div>
              <div className={styles["apt-pagination-controls"]}>
                <button
                  className={`${styles["apt-pagination-btn"]} ${withdrawalsPage === 1 ? styles["disabled"] : ""}`}
                  onClick={() => setWithdrawalsPage((p) => Math.max(1, p - 1))}
                  disabled={withdrawalsPage === 1 || loadingWithdrawals}
                >
                  {t("dashboard.pagination.previous")}
                </button>
                <div className={styles["apt-page-numbers"]}>
                  {Array.from({ length: withdrawalsTotalPages }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        className={`${styles["apt-page-btn"]} ${styles[`${withdrawalsPage === page ? "active" : ""}`]}`}
                        onClick={() => setWithdrawalsPage(page)}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                <button
                  className={`${styles["apt-pagination-btn"]} ${styles["apt-next-btn"]} ${withdrawalsPage === withdrawalsTotalPages ? styles["disabled"] : ""}`}
                  onClick={() => setWithdrawalsPage((p) => Math.min(withdrawalsTotalPages, p + 1))}
                  disabled={withdrawalsPage === withdrawalsTotalPages || loadingWithdrawals}
                >
                  {t("dashboard.pagination.next")}
                </button>
              </div>
            </div>
          </>
        )}

        {/* Pagination */}
        {activeTab === "Earnings" && (
        <div className={styles["apt-pagination"]}>
          <div className={styles["apt-pagination-info"]}>
            {t("dashboard.pagination.showing")}{" "}
            {(currentPage - 1) * itemsPerPage + 1}{" "}
            {t("dashboard.pagination.to")}{" "}
            {(currentPage - 1) * itemsPerPage + paginatedAppointments.length}{" "}
            {t("dashboard.pagination.of")} {totalPages * itemsPerPage}
          </div>
          <div className={styles["apt-pagination-controls"]}>
            <button
              className={`${styles["apt-pagination-btn"]} ${
                currentPage === 1 ? styles["disabled"] : ""
              }`}
              // onClick={() => handlePageChange(currentPage - 1)}
              // onClick={() => handlePageChange11(currentPage)}
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              {t("dashboard.pagination.previous")}
            </button>
            <div className={styles["apt-page-numbers"]}>
              {/* {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  className={`${styles["apt-page-btn"]} ${
                    styles[`${currentPage === page ? "active" : ""}`]
                  }`}
                  onClick={() => handlePageChange(currentPage)}
                >
                  {page}
                </button>
              );
            })} */}

              {Array.from({ length: totalPages }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    className={`${styles["apt-page-btn"]} ${
                      styles[`${currentPage === page ? "active" : ""}`]
                    }`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            <button
              className={`${styles["apt-pagination-btn"]} ${
                styles["apt-next-btn"]
              } ${currentPage === totalPages ? styles["disabled"] : ""}`}
              // onClick={() => handlePageChange(currentPage + 1)}
              // onClick={() => handlePageChange1(currentPage)}
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              {t("dashboard.pagination.next")}
            </button>
          </div>
        </div>
        )}
      </div>{" "}
    </PhysioDashboardLayout>
  );
};

export default PhysioEarningPayment;
